"use client";

import React, { useMemo, useState, useEffect, useRef } from "react";
import { Check, X, Sparkles, Zap, Crown, Minus, Plus, ShieldCheck, Globe2, ChevronDown, WandSparkles } from "lucide-react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useLanguage } from "@/contexts/LanguageContext";
import { getStrings } from "@/i18n/dictionaries";
import { localizedCountryName } from "@/i18n/deviceSetupContent";
import { formatPricingPremiumText, getPricingPremiumContent } from "@/i18n/pricingPremiumContent";
import { SUPPORT_EMAIL, ERROR_MESSAGES } from "@/lib/constants/support";
import pricingData from '../../.Data/pricing_table.json';
import BankTransferDetails from "../payment/BankTransferDetails";
import MobileWalletDetails from "../payment/MobileWalletDetails";
import PaymentMethodModal from "../payment/PaymentMethodModal";
import FlutterwaveLoadingOverlay from "../payment/FlutterwaveLoadingOverlay";
import ConfirmBeforePaymentModal from "../payment/ConfirmBeforePaymentModal";
import CompleteProfilePrompt from "../payment/CompleteProfilePrompt";
import NigeriaPhonePrompt from "../payment/NigeriaPhonePrompt";
import GhanaPhonePrompt from "../payment/GhanaPhonePrompt";
import KenyaPhonePrompt from "../payment/KenyaPhonePrompt";
import RwandaPhonePrompt from "../payment/RwandaPhonePrompt";
import TanzaniaPhonePrompt from "../payment/TanzaniaPhonePrompt";
import UgandaPhonePrompt from "../payment/UgandaPhonePrompt";
import ZambiaPhonePrompt from "../payment/ZambiaPhonePrompt";
import SouthAfricaPhonePrompt from "../payment/SouthAfricaPhonePrompt";
import EgyptPhonePrompt from "../payment/EgyptPhonePrompt";
import FrancophonePhonePrompt from "../payment/FrancophonePhonePrompt";
import { getCountryPaymentConfig, isMethodEnabled, PAYMENT_METHOD } from "@/lib/flutterwave_v4/payment_config";
import { getSubCountries, getSubCountryOperators } from "@/lib/flutterwave_v4/momo_shared";
import { handlePlanCheckout } from "@/lib/stripe/handleCheckout";
import { EMAIL_AUTO_APPLY_COUNTRIES, EMAIL_AUTO_APPLY_COUNTRY_COUNT, supportedCountriesHref } from "@/config/supportedCountries";
import {
  calculateInterviewCopilotOnlyPrice,
  INTERVIEW_COPILOT_ONLY_DEFAULT_HOURS,
  INTERVIEW_COPILOT_ONLY_MAX_HOURS,
  INTERVIEW_COPILOT_ONLY_MIN_HOURS,
  INTERVIEW_COPILOT_ONLY_PLAN_ID,
} from "@/lib/billing/interviewCopilotOnly";
import { getClassifiedError } from "@/lib/flutterwave_v4/error_classifier";

const DURATION_KEYS = ["monthly", "quarterly", "semiannual"];

// Ribbon tag ID → icon mapping
const TAG_ICON_MAP = {
  basic: Zap,
  standard: Sparkles,
  pro: Crown,
};

// Wallet type constants
const WALLET_TYPE_OPAY = "opay";
const WALLET_TYPE_GHANA_MOMO = "momo-gh";
const WALLET_TYPE_KENYA_MOMO = "momo-ke";
const WALLET_TYPE_RWANDA_MOMO = "momo-rw";
const WALLET_TYPE_TANZANIA_MOMO = "momo-tz";
const WALLET_TYPE_UGANDA_MOMO = "momo-ug";
const WALLET_TYPE_ZAMBIA_MOMO = "momo-zm";
const WALLET_TYPE_XOF_MOMO = "momo-xof";
const WALLET_TYPE_XAF_MOMO = "momo-xaf";

// ── localStorage key for resuming pending MoMo payment after refresh ──
const PENDING_MOMO_STORAGE_KEY = "jobolar_pending_momo_payment";

/**
 * Persist a pending MoMo payment to localStorage so the polling / authorize
 * UI can resume after a page refresh or navigation.
 */
function savePendingMomoPayment(walletData, walletType, countryCode) {
  try {
    const payload = {
      walletData: {
        tx_ref: walletData.tx_ref,
        amount: walletData.amount,
        currency: walletData.currency,
        status: walletData.status,
        instructions: walletData.instructions || null,
        authorizationRedirect: walletData.authorizationRedirect || null,
        payment_url: walletData.payment_url || null,
        reference: walletData.reference || null,
      },
      walletType,
      countryCode: countryCode || null,
      savedAt: Date.now(),
    };
    localStorage.setItem(PENDING_MOMO_STORAGE_KEY, JSON.stringify(payload));
  } catch (_) { /* quota / SSR */ }
}

function clearPendingMomoPayment() {
  try { localStorage.removeItem(PENDING_MOMO_STORAGE_KEY); } catch (_) { /* SSR */ }
}

/**
 * Load a previously saved pending MoMo payment.
 * Returns null if nothing stored or if the entry is older than 30 minutes.
 */
function loadPendingMomoPayment() {
  try {
    const raw = localStorage.getItem(PENDING_MOMO_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Expire after 30 minutes — polling would have timed out by then.
    if (!parsed.savedAt || Date.now() - parsed.savedAt > 30 * 60 * 1000) {
      clearPendingMomoPayment();
      return null;
    }
    if (!parsed.walletData?.tx_ref) {
      clearPendingMomoPayment();
      return null;
    }
    return parsed;
  } catch (_) {
    clearPendingMomoPayment();
    return null;
  }
}

// Mapping for flattened Francophone country codes to their internal region codes.
// When the user selects BF/CI/SN/CM directly in the dropdown, we map them
// to the XOF/XAF region that the backend expects.
const FRANCOPHONE_COUNTRY_TO_REGION = {
  BF: "XOF",
  CI: "XOF",
  SN: "XOF",
  CM: "XAF",
};

// Dial prefixes for MoMo phone validation (E.164). Add here to support more countries in the future.
const MOMO_DIAL_PREFIX_BY_COUNTRY = {
  NG: "+234",
  GH: "+233",
  KE: "+254",
  RW: "+250",
  TZ: "+255",
  UG: "+256",
  ZM: "+260",
  ZA: "+27",
  EG: "+20",
  BF: "+226",
  CI: "+225",
  SN: "+221",
  CM: "+237",
};

// Country code → display name (used for profile-country validation messaging)
const COUNTRY_CODE_TO_NAME = {
  NG: "Nigeria",
  GH: "Ghana",
  KE: "Kenya",
  RW: "Rwanda",
  TZ: "Tanzania",
  UG: "Uganda",
  ZM: "Zambia",
  ZA: "South Africa",
  EG: "Egypt",
  BF: "Burkina Faso",
  CI: "Côte d'Ivoire",
  SN: "Senegal",
  CM: "Cameroon",
};

// Network display names for MoMo providers
const MOMO_NETWORK_DISPLAY_NAMES = {
  mtn: "MTN",
  MTN: "MTN",
  airtel: "Airtel Money",
  AIRTEL: "Airtel Money",
  vodafone: "Vodafone",
  halopesa: "Halopesa",
  tigo: "AirtelTigo",  // Map 'tigo' API key to 'AirtelTigo' display
  mpesa: "M-Pesa",
  mps: "M-Pesa",
  zamtel: "Zamtel",
  ZAMTEL: "Zamtel",
  orange_money: "Orange Money",
  ORANGEMONEY: "Orange Money",
  mobicash: "Mobicash",
  MOBICASH: "Mobicash",
  orange: "Orange",
  ORANGE: "Orange",
  moov: "Moov",
  MOOV: "Moov",
  wave: "Wave",
  WAVE: "Wave",
  free_money: "Free Money",
  e_money: "E Money",
};

// Map language codes to API expected codes (supported: en, fr, es, de)
const languageToApiCode = {
  en: "us",
  fr: "fr",
  es: "es",
  de: "de",
};

function normalizeDuration(v) {
  return DURATION_KEYS.includes(v) ? v : "monthly";
}

function SupportedCountriesLink({ label }) {
  return (
    <a
      href={supportedCountriesHref("/#pricing")}
      className="font-bold text-current underline decoration-current/35 underline-offset-2 transition hover:decoration-current focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5A4AF4] focus-visible:ring-offset-2"
    >
      {label}
    </a>
  );
}

function FeatureText({ text, supportedCountriesText }) {
  const phrase = supportedCountriesText;
  const index = text.indexOf(phrase);
  if (index < 0) return text;
  return (
    <>
      {text.slice(0, index)}
      <SupportedCountriesLink label={phrase} />
      {text.slice(index + phrase.length)}
    </>
  );
}

function PriceBlock({ value, original, duration, copy, dark = false }) {
  const shortKey = duration === "monthly" ? "shortMonthly" : duration === "quarterly" ? "shortQuarterly" : "shortSemiannual";
  const durationShort = copy.billing[shortKey];
  return (
    <div className="mt-2 text-center">
      <div className="flex items-end justify-center gap-1">
        <div className={`text-4xl font-black tracking-tight ${dark ? "text-white" : "text-slate-950"}`}>${Number(value).toFixed(2)}</div>
        <div className={`pb-1 text-sm font-bold ${dark ? "text-violet-200" : "text-slate-600"}`}>{formatPricingPremiumText(copy.labels.pricePerTerm, { term: durationShort })}</div>
      </div>
      <div className={`mt-1 flex items-center justify-center gap-2 text-sm font-extrabold ${dark ? "text-violet-200" : "text-slate-600"}`}>
        <span className="line-through">${Number(original).toFixed(2)}</span>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-black ${dark ? "bg-amber-300/15 text-amber-200" : "bg-emerald-50 text-emerald-700"}`}>{formatPricingPremiumText(copy.labels.savePercent, { percent: Number(pricingData.promoPercent) })}</span>
      </div>
    </div>
  );
}

function DiscountBadge({ copy }) {
  return (
    <span className="jobolar-discount-badge relative inline-flex overflow-hidden rounded-full border border-amber-200/70 bg-gradient-to-r from-amber-200 via-yellow-50 to-amber-300 px-3 py-1 text-[11px] font-black tracking-[0.18em] text-[#4a3300] shadow-[0_0_24px_rgba(251,191,36,0.28)]">
      <span className="jobolar-discount-shine absolute inset-y-0 -left-1/2 w-1/3 -skew-x-12 bg-white/70" aria-hidden="true" />
      <span className="relative">{formatPricingPremiumText(copy.page.discountBadge, { percent: Number(pricingData.promoPercent) })}</span>
    </span>
  );
}

function LuxuryPlanCard({ plan, price, duration, copy, loading, onChoose }) {
  const isStandard = plan.id === "standard";
  const isPro = plan.id === "pro";
  const Icon = TAG_ICON_MAP[plan.id] || Zap;
  return (
    <article className={`group relative flex h-full min-w-0 flex-col overflow-hidden rounded-[28px] border p-5 transition duration-300 hover:-translate-y-1 sm:p-6 ${
      isPro
        ? "border-violet-300/70 bg-gradient-to-b from-[#201445] via-[#2b1858] to-[#160e31] text-white shadow-[0_24px_70px_rgba(54,31,116,0.30)]"
        : isStandard
          ? "border-[#7c70ff]/70 bg-white shadow-[0_24px_70px_rgba(90,74,244,0.20)] ring-2 ring-[#5A4AF4]/20"
          : "border-white/80 bg-white/90 shadow-[0_20px_60px_rgba(35,27,80,0.12)] backdrop-blur"
    }`}>
      <div className={`absolute inset-x-0 top-0 h-1 ${isPro ? "bg-gradient-to-r from-amber-300 via-violet-300 to-fuchsia-300" : "bg-gradient-to-r from-[#5A4AF4] via-violet-400 to-cyan-300"}`} />
      <div className="flex items-start justify-between gap-3">
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.12em] ${isPro ? "bg-white/10 text-amber-200 ring-1 ring-white/15" : "bg-violet-50 text-[#5A4AF4] ring-1 ring-violet-100"}`}>
          <Icon size={13} /> {plan.tag}
        </span>
        <DiscountBadge copy={copy} />
      </div>
      <h3 className={`mt-5 text-2xl font-black tracking-tight ${isPro ? "text-white" : "text-slate-950"}`}>{plan.name}</h3>
      <p className={`mt-2 min-h-[5.25rem] text-sm leading-6 ${isPro ? "text-violet-100" : "text-slate-600"}`}>{plan.description}</p>
      <div className={`mt-5 rounded-2xl border p-4 ${isPro ? "border-white/10 bg-white/[0.07]" : "border-violet-100 bg-gradient-to-br from-violet-50/80 to-white"}`}>
        <PriceBlock value={price.final} original={price.original} duration={duration} copy={copy} dark={isPro} />
      </div>
      <ul className="mt-5 space-y-3">
        {plan.featuredFeatures.map((feature) => (
          <li key={feature} className={`flex min-w-0 items-start gap-2.5 text-sm leading-5 ${isPro ? "text-violet-50" : "text-slate-700"}`}>
            <span className={`mt-0.5 shrink-0 rounded-full p-1 ${isPro ? "bg-amber-300/15 text-amber-200" : "bg-emerald-50 text-emerald-600"}`}><Check size={13} /></span>
            <span className="min-w-0"><FeatureText text={feature} supportedCountriesText={formatPricingPremiumText(copy.countries.supportedLink, { count: EMAIL_AUTO_APPLY_COUNTRY_COUNT })} /></span>
          </li>
        ))}
      </ul>
      <details className={`mt-5 rounded-2xl border ${isPro ? "border-white/10 bg-white/[0.04]" : "border-slate-200 bg-slate-50/80"}`}>
        <summary className={`flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 text-sm font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5A4AF4] ${isPro ? "text-violet-100" : "text-slate-700"}`}>
          {copy.labels.planDetails} <ChevronDown size={16} aria-hidden="true" />
        </summary>
        <ul className={`space-y-2 border-t px-4 py-4 ${isPro ? "border-white/10" : "border-slate-200"}`}>
          {plan.features.map((feature) => (
            <li key={feature} className={`flex items-start gap-2 text-xs leading-5 ${isPro ? "text-violet-100" : "text-slate-600"}`}><Check className="mt-0.5 shrink-0 text-[#5A4AF4]" size={13} /><span><FeatureText text={feature} supportedCountriesText={formatPricingPremiumText(copy.countries.supportedLink, { count: EMAIL_AUTO_APPLY_COUNTRY_COUNT })} /></span></li>
          ))}
        </ul>
      </details>
      <div className="mt-auto pt-6">
        <button
          type="button"
          onClick={onChoose}
          disabled={loading}
          className={`w-full rounded-2xl px-4 py-3.5 text-sm font-extrabold shadow-lg transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60 ${isPro ? "bg-gradient-to-r from-amber-300 to-yellow-200 text-[#291b00] hover:brightness-105" : "bg-gradient-to-r from-[#5A4AF4] to-[#7868ff] text-white hover:brightness-110"}`}
        >
          {loading ? copy.labels.loading : isPro ? copy.labels.unlockCompleteExperience : copy.labels.choosePlan}
        </button>
        <p className={`mt-2 text-center text-[11px] ${isPro ? "text-violet-200" : "text-slate-500"}`}>{copy.labels.cancelNote}</p>
      </div>
    </article>
  );
}

function PricingFaq({ copy, locale }) {
  return (
    <section aria-labelledby="pricing-faq-title" className="mx-auto mt-12 max-w-5xl">
      <div className="text-center">
        <p className="text-xs font-black uppercase tracking-[0.22em] text-[#5A4AF4]">{copy.faq.eyebrow}</p>
        <h2 id="pricing-faq-title" className="mt-2 text-2xl font-black text-slate-950 sm:text-3xl">{copy.faq.title}</h2>
      </div>
      <div className="mt-6 grid gap-3 md:grid-cols-2">
        {copy.faq.items.map(({ id, question, answer }) => (
          <details key={id} className="group rounded-2xl border border-white/80 bg-white/85 p-5 shadow-[0_12px_35px_rgba(45,32,92,0.08)] backdrop-blur">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 font-bold text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5A4AF4]"><span>{question}</span><ChevronDown className="shrink-0 transition group-open:rotate-180" size={18} /></summary>
            <p className="mt-3 text-sm leading-6 text-slate-600">{answer}</p>
          </details>
        ))}
      </div>
      <div id="email-auto-apply-countries" className="mt-5 scroll-mt-24 rounded-[28px] border border-violet-200/70 bg-gradient-to-br from-white to-violet-50 p-6 shadow-[0_18px_50px_rgba(90,74,244,0.10)]">
        <div className="flex items-center gap-3"><span className="rounded-xl bg-[#5A4AF4] p-2 text-white"><Globe2 size={20} /></span><div><h3 className="font-black text-slate-950">{formatPricingPremiumText(copy.countries.title, { count: EMAIL_AUTO_APPLY_COUNTRY_COUNT })}</h3><p className="text-sm text-slate-600">{copy.countries.description}</p></div></div>
        <div className="mt-5 grid gap-5 sm:grid-cols-3">
          {Object.entries(EMAIL_AUTO_APPLY_COUNTRIES).map(([region, countries]) => (
            <div key={region}><h4 className="text-xs font-black uppercase tracking-[0.14em] text-[#5A4AF4]">{copy.countries.regions[region] || region}</h4><p className="mt-2 text-xs leading-5 text-slate-600">{countries.map((country) => localizedCountryName(country, locale)).join(" · ")}</p></div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function PricingTable({ context = "public", isAuthenticated = false }) {
  const [duration, setDuration] = useState("monthly");
  const [interviewHoursPerMonth, setInterviewHoursPerMonth] = useState(INTERVIEW_COPILOT_ONLY_DEFAULT_HOURS);
  const [interviewHoursInput, setInterviewHoursInput] = useState(
    String(INTERVIEW_COPILOT_ONLY_DEFAULT_HOURS)
  );
  const [loading, setLoading] = useState(null);
  const [bankTransferData, setBankTransferData] = useState(null); // Bank transfer details
  const [mobileWalletData, setMobileWalletData] = useState(null); // Mobile wallet details (OPay, MoMo, etc.)
  const [walletType, setWalletType] = useState(null); // Wallet type: WALLET_TYPE_OPAY or WALLET_TYPE_MOMO
  const [restoredCountryCode, setRestoredCountryCode] = useState(null); // Country code restored from localStorage
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showFlutterwaveLoading, setShowFlutterwaveLoading] = useState(false); // Loading overlay for Flutterwave
  const [flutterwaveError, setFlutterwaveError] = useState(null);
  const [flutterwaveErrorCategory, setFlutterwaveErrorCategory] = useState(null);
  const [profileError, setProfileError] = useState(null); // { errorCode, missingFields, message }
  const [blacklistError, setBlacklistError] = useState(null); // { message, supportEmail }
  const [showNigeriaPhonePrompt, setShowNigeriaPhonePrompt] = useState(false); // Nigeria phone prompt for OPay
  const [showGhanaPhonePrompt, setShowGhanaPhonePrompt] = useState(false); // Ghana phone prompt for MoMo
  const [showKenyaPhonePrompt, setShowKenyaPhonePrompt] = useState(false); // Kenya phone prompt for M-Pesa
  const [showRwandaPhonePrompt, setShowRwandaPhonePrompt] = useState(false); // Rwanda phone prompt for MoMo
  const [showTanzaniaPhonePrompt, setShowTanzaniaPhonePrompt] = useState(false); // Tanzania phone prompt for MoMo
  const [showUgandaPhonePrompt, setShowUgandaPhonePrompt] = useState(false); // Uganda phone prompt for MoMo
  const [showZambiaPhonePrompt, setShowZambiaPhonePrompt] = useState(false); // Zambia phone prompt for MoMo
  const [showSouthAfricaPhonePrompt, setShowSouthAfricaPhonePrompt] = useState(false); // South Africa phone prompt for MoMo
  const [showEgyptPhonePrompt, setShowEgyptPhonePrompt] = useState(false); // Egypt phone prompt for MoMo
  const [showFrancophonePhonePrompt, setShowFrancophonePhonePrompt] = useState(false); // Francophone phone prompt for XOF/XAF MoMo
  const [francophonePhoneSubCountry, setFrancophonePhoneSubCountry] = useState(null); // Sub-country code for Francophone phone prompt (BF/CI/SN/CM)
  const [selectedCountry, setSelectedCountry] = useState(null); // Selected country for Flutterwave payment { code, name }
  const [networkSelectionData, setNetworkSelectionData] = useState(null); // Network selection modal data: { supportedNetworks, countryCode, retryPayload }
  const [subCountrySelectionData, setSubCountrySelectionData] = useState(null); // Sub-country selection for XOF/XAF: { subCountries, regionCode, retryPayload }
  const [resolvedIdentity, setResolvedIdentity] = useState({ phone: null, authPhone: null, profilePhone: null, profileCountry: null, email: null, name: null, loading: true }); // Resolved customer identity
  const [trialState, setTrialState] = useState(null);
  const [trialLoading, setTrialLoading] = useState(false);
  const [trialError, setTrialError] = useState("");
  const lastFlutterwaveAttemptRef = useRef(null); // Stores { gateway, paymentCountry, flutterwaveMethod } for retry
  const { data: session, status } = useSession();
  const router = useRouter();
  const { locale } = useLanguage();
  const t = useMemo(() => getStrings(locale, "pricing"), [locale]);
  const tc = useMemo(() => getStrings(locale, "copilot_product"), [locale]);
  const tp = useMemo(() => getStrings(locale, "payment"), [locale]);
  const premium = useMemo(() => getPricingPremiumContent(locale), [locale]);
  const durations = useMemo(() => DURATION_KEYS.map((k) => ({
    key: k,
    label: premium.billing[k] || k,
    short: premium.billing[k === "monthly" ? "shortMonthly" : k === "quarterly" ? "shortQuarterly" : "shortSemiannual"],
  })), [premium]);
  const safeDuration = normalizeDuration(duration);

  const handleDurationKeyDown = (event, currentIndex) => {
    const key = event.key;
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(key)) return;
    event.preventDefault();
    const nextIndex = key === "Home"
      ? 0
      : key === "End"
        ? durations.length - 1
        : (currentIndex + (key === "ArrowRight" ? 1 : -1) + durations.length) % durations.length;
    setDuration(normalizeDuration(durations[nextIndex].key));
    requestAnimationFrame(() => {
      document.querySelector(`[data-billing-tab="${durations[nextIndex].key}"]`)?.focus();
    });
  };

  useEffect(() => {
    if (status !== "authenticated") return;
    let cancelled = false;
    fetch("/api/trial", { cache: "no-store" })
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.error || "Unable to check trial availability.");
        if (!cancelled) setTrialState(body);
      })
      .catch((error) => {
        if (!cancelled) setTrialError(error.message || "Unable to check trial availability.");
      });
    return () => { cancelled = true; };
  }, [status]);

  const handleStartTrial = async () => {
    if (status !== "authenticated") {
      router.push("/signup?callbackUrl=%2F%23pricing");
      return;
    }
    setTrialLoading(true);
    setTrialError("");
    try {
      const response = await fetch("/api/trial", { method: "POST" });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || "Unable to start the free trial.");
      setTrialState(body);
      router.push("/onboarding");
    } catch (error) {
      setTrialError(error.message || "Unable to start the free trial.");
    } finally {
      setTrialLoading(false);
    }
  };

  const updateInterviewHours = (hours) => {
    const nextHours = Math.min(
      INTERVIEW_COPILOT_ONLY_MAX_HOURS,
      Math.max(INTERVIEW_COPILOT_ONLY_MIN_HOURS, hours)
    );
    setInterviewHoursPerMonth(nextHours);
    setInterviewHoursInput(String(nextHours));
  };

  const handleInterviewHoursInput = (event) => {
    const value = event.target.value;
    if (value !== "" && !/^\d+$/.test(value)) return;

    setInterviewHoursInput(value);
    const parsed = Number(value);
    if (
      Number.isInteger(parsed) &&
      parsed >= INTERVIEW_COPILOT_ONLY_MIN_HOURS &&
      parsed <= INTERVIEW_COPILOT_ONLY_MAX_HOURS
    ) {
      setInterviewHoursPerMonth(parsed);
    }
  };

  const commitInterviewHoursInput = () => {
    const parsed = Number(interviewHoursInput);
    if (!Number.isInteger(parsed) || interviewHoursInput.trim() === "") {
      setInterviewHoursInput(String(interviewHoursPerMonth));
      return;
    }
    updateInterviewHours(parsed);
  };

  // Determine if user is authenticated
  const userIsAuthenticated = status === "authenticated" || isAuthenticated;

  // Map locale to API language code
  const apiLanguage = languageToApiCode[locale] || "us";

  // Helper function to normalize phone for validation
  const normalizePhoneForValidation = (phone, countryHint) => {
    if (!phone) return "";
    
    // Trim and remove common formatting characters
    let normalized = phone.trim().replace(/[\s\-()]/g, "");
    
    const hintedCountry = (countryHint || "").toUpperCase();

    // Handle international prefix variant (00...)
    if (normalized.startsWith("00")) {
      normalized = "+" + normalized.slice(2);
    }

    // Generic normalization for countries with known dial prefixes:
    // - convert local leading 0 to +<dial>
    // - add missing '+' when phone starts with dial digits
    const dialPrefix = MOMO_DIAL_PREFIX_BY_COUNTRY[hintedCountry];
    if (dialPrefix) {
      const dialDigits = dialPrefix.replace("+", "");

      if (!normalized.startsWith("+") && normalized.startsWith(dialDigits)) {
        normalized = "+" + normalized;
      }

      if (normalized.startsWith("0") && normalized.length >= 8) {
        // Nigeria is a special case: only convert 0XXXXXXXXXX -> +234XXXXXXXXXX when
        // the number length matches the Nigerian local format (11 digits).
        // Otherwise we'd incorrectly convert other countries' local numbers (often 10 digits) into +234.
        if (hintedCountry !== "NG" || normalized.length === 11) {
          normalized = dialPrefix + normalized.slice(1);
        }
      }
    }

    // Convert Nigerian local format (0xxx) to international (+234xxx)
    if ((hintedCountry === "" || hintedCountry === "NG") && normalized.startsWith("0") && normalized.length === 11) {
      normalized = "+234" + normalized.slice(1);
    }

    // Convert Kenyan local format (07xxxxxxxx) to international (+2547xxxxxxxx)
    if (hintedCountry === "KE" && normalized.startsWith("07") && normalized.length === 10) {
      normalized = "+254" + normalized.slice(1);
    }

    // Convert Kenyan local format (7xxxxxxxx) to international (+2547xxxxxxxx)
    if (hintedCountry === "KE" && normalized.startsWith("7") && normalized.length === 9) {
      normalized = "+254" + normalized;
    }

    // Convert Kenyan format without plus (2547xxxxxxxx) to E.164
    if (hintedCountry === "KE" && normalized.startsWith("254") && normalized.length === 12) {
      normalized = "+" + normalized;
    }

    // Convert Rwanda local format (07xxxxxxxx) to international (+2507xxxxxxxx)
    if (hintedCountry === "RW" && normalized.startsWith("07") && normalized.length === 10) {
      normalized = "+250" + normalized.slice(1);
    }

    // Convert Rwanda local format (7xxxxxxxx) to international (+2507xxxxxxxx)
    if (hintedCountry === "RW" && normalized.startsWith("7") && normalized.length === 9) {
      normalized = "+250" + normalized;
    }

    // Convert Rwanda format without plus (2507xxxxxxxx) to E.164
    if (hintedCountry === "RW" && normalized.startsWith("250") && normalized.length === 12) {
      normalized = "+" + normalized;
    }

    // Convert Tanzania local format (06xxxxxxxx / 07xxxxxxxx) to international (+2556xxxxxxxx / +2557xxxxxxxx)
    if (hintedCountry === "TZ" && normalized.startsWith("0") && normalized.length === 10) {
      normalized = "+255" + normalized.slice(1);
    }

    // Convert Tanzania local format (6xxxxxxxx / 7xxxxxxxx) to international (+2556xxxxxxxx / +2557xxxxxxxx)
    if (hintedCountry === "TZ" && (normalized.startsWith("6") || normalized.startsWith("7")) && normalized.length === 9) {
      normalized = "+255" + normalized;
    }

    // Convert Tanzania format without plus (2557xxxxxxxx) to E.164
    if (hintedCountry === "TZ" && normalized.startsWith("255") && normalized.length === 12) {
      normalized = "+" + normalized;
    }

    // Convert South Africa local format (0XXXXXXXXX) to international (+27XXXXXXXXX)
    if (hintedCountry === "ZA" && normalized.startsWith("0") && normalized.length === 10) {
      normalized = "+27" + normalized.slice(1);
    }

    // Convert South Africa format without plus (27XXXXXXXXX) to E.164
    if (hintedCountry === "ZA" && normalized.startsWith("27") && normalized.length === 11) {
      normalized = "+" + normalized;
    }

    // Convert Egypt local format (0XXXXXXXXXX) to international (+20XXXXXXXXXX)
    if (hintedCountry === "EG" && normalized.startsWith("0") && normalized.length === 11) {
      normalized = "+20" + normalized.slice(1);
    }

    // Convert Egypt format without plus (20XXXXXXXXXX) to E.164
    if (hintedCountry === "EG" && normalized.startsWith("20") && normalized.length === 12) {
      normalized = "+" + normalized;
    }
    
    return normalized;
  };

  const phoneMatchesMomoCountry = (phone, countryCode) => {
    const code = (countryCode || "").toUpperCase();
    const expectedPrefix = MOMO_DIAL_PREFIX_BY_COUNTRY[code];
    if (!expectedPrefix) return true; // Don't block unknown/future codes client-side
    const normalized = normalizePhoneForValidation(phone, code);
    return Boolean(normalized && normalized.startsWith(expectedPrefix));
  };

  /**
   * Set a classified Flutterwave error — updates both the message and category
   * so FlutterwaveLoadingOverlay can render the appropriate UI.
   *
   * @param {string} rawMessage  - The raw error message (from FLW or client)
   * @param {string|null} backendCategory - If the backend already classified it, pass it through
   */
  const setClassifiedFlutterwaveError = (rawMessage, backendCategory = null) => {
    if (!rawMessage) {
      setFlutterwaveError(null);
      setFlutterwaveErrorCategory(null);
      return;
    }
    if (backendCategory) {
      setFlutterwaveErrorCategory(backendCategory);
      // For merchant_restriction, the backend already replaced the message
      setFlutterwaveError(rawMessage);
    } else {
      const classified = getClassifiedError(rawMessage);
      setFlutterwaveErrorCategory(classified.category);
      setFlutterwaveError(classified.userMessage);
    }
  };

  // Helper function to check if phone is Nigerian
  const isNigeriaPhone = (phone) => {
    if (!phone) return false;
    const normalized = normalizePhoneForValidation(phone, "NG");
    return normalized.startsWith("+234");
  };

  // Helper function to check if phone is Ghanaian
  const isGhanaPhone = (phone) => {
    if (!phone) return false;
    const normalized = normalizePhoneForValidation(phone);
    return normalized.startsWith("+233");
  };

  // Helper function to check if phone is Kenyan
  const isKenyaPhone = (phone) => {
    if (!phone) return false;
    const normalized = normalizePhoneForValidation(phone, "KE");
    return normalized.startsWith("+254");
  };

  // Helper function to check if phone is Rwandan
  const isRwandaPhone = (phone) => {
    if (!phone) return false;
    const normalized = normalizePhoneForValidation(phone, "RW");
    return normalized.startsWith("+250");
  };

  // Helper function to check if phone is Tanzanian
  const isTanzaniaPhone = (phone) => {
    if (!phone) return false;
    const normalized = normalizePhoneForValidation(phone, "TZ");
    return normalized.startsWith("+255");
  };

  /**
   * Normalize a country string for alias-tolerant matching.
   * Lowercases, strips accents/diacritics, removes punctuation, collapses whitespace,
   * then resolves known aliases to a canonical key.
   */
  const normalizeCountryForMatch = (input) => {
    if (!input) return "";
    // lowercase → strip diacritics → remove non-alphanumeric (except space) → collapse whitespace
    const stripped = input
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")   // remove accent marks
      .replace(/[^a-z0-9 ]/g, "")        // remove punctuation / apostrophes
      .replace(/\s+/g, " ")
      .trim();
    // Alias map: alternative spellings → canonical key
    const COUNTRY_ALIASES = {
      "ivory coast":   "cote divoire",
      "cote d ivoire": "cote divoire",   // after apostrophe removal
      "cote divoire":  "cote divoire",
      "dr congo":      "democratic republic of the congo",
      "drc":           "democratic republic of the congo",
    };
    return COUNTRY_ALIASES[stripped] || stripped;
  };

  /**
   * Check if the user's profile country matches the required payment country.
   * Uses normalizeCountryForMatch so alias variants (e.g. "Côte d'Ivoire" vs
   * "Ivory Coast") are treated as equal.  Also accepts ISO code match.
   *
   * @param {string} profileCountry - The user's profile country from Firestore (display name)
   * @param {string} requiredCountryCode - The required country code (e.g. "NG", "GH")
   * @param {string} requiredCountryName - The required country display name (e.g. "Nigeria")
   * @returns {boolean} true if profile country matches
   */
  const profileCountryMatchesRequired = (profileCountry, requiredCountryCode, requiredCountryName) => {
    if (!profileCountry) return false;
    const normalizedProfile = profileCountry.trim().toLowerCase();
    if (!normalizedProfile) return false;
    // 1. ISO code match (profile might store "NG" instead of "Nigeria")
    if (requiredCountryCode && normalizedProfile === requiredCountryCode.toLowerCase()) return true;
    // 2. Alias-tolerant display-name match
    if (requiredCountryName) {
      const normProfile = normalizeCountryForMatch(profileCountry);
      const normRequired = normalizeCountryForMatch(requiredCountryName);
      if (normProfile && normRequired && normProfile === normRequired) return true;
    }
    return false;
  };

  // ── Restore pending MoMo payment from localStorage after refresh/navigation ──
  useEffect(() => {
    const saved = loadPendingMomoPayment();
    if (!saved) return;
    console.log("[PricingTable] Restoring pending MoMo payment from localStorage:", saved.walletData.tx_ref);
    setMobileWalletData(saved.walletData);
    setWalletType(saved.walletType);
    if (saved.countryCode) {
      setRestoredCountryCode(saved.countryCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch and resolve customer identity when authenticated
  useEffect(() => {
    /**
     * Resolve customer identity from Firestore and Firebase Auth
     * This is the single source of truth for customer data used in all payment methods
     */
    const resolveCustomerIdentity = async () => {
      if (!userIsAuthenticated || !session?.user?.id) {
        return { phone: null, authPhone: null, profilePhone: null, profileCountry: null, email: null, name: null };
      }

      try {
        // Fetch from Firestore profile (same source backend uses)
        const response = await fetch('/api/user/profile');
        if (response.ok) {
          const data = await response.json();
          const profile = data?.profile || {};
          
          // Build full name (consistent with backend logic)
          let fullName = "";
          if (profile.firstName && profile.lastName) {
            fullName = profile.middleName 
              ? `${profile.firstName} ${profile.middleName} ${profile.lastName}`
              : `${profile.firstName} ${profile.lastName}`;
          } else if (profile.firstName) {
            fullName = profile.firstName;
          } else if (profile.lastName) {
            fullName = profile.lastName;
          }
          
          // Fallback to email local part if no name
          if (!fullName && session?.user?.email) {
            fullName = session.user.email.split("@")[0].replace(/[._-]/g, " ").trim();
            fullName = fullName.charAt(0).toUpperCase() + fullName.slice(1);
          }
          
          // Track both sources separately for debugging
          // Use empty strings (not null) after fetch to indicate data is loaded but may be empty
          const authPhone = session?.user?.phoneNumber?.trim() || "";
          const profilePhone = profile.phoneNumber?.trim() || "";
          
          // Get phone: try Firestore profile first (same source backend uses), then Firebase Auth
          const phoneNumber = profilePhone || authPhone;
          
          return {
            phone: phoneNumber,
            authPhone: authPhone,
            profilePhone: profilePhone,
            profileCountry: profile.currentCountry || "",
            email: session?.user?.email || "",
            name: fullName,
          };
        }
      } catch (error) {
        console.error("[PricingTable] Failed to resolve customer identity:", error);
      }
      
      // Fallback to session data only
      const authPhone = session?.user?.phoneNumber || "";
      return {
        phone: authPhone,
        authPhone: authPhone,
        profilePhone: "",
        profileCountry: "",
        email: session?.user?.email || "",
        name: session?.user?.name || "",
      };
    };

    if (userIsAuthenticated && session?.user?.id) {
      const fetchIdentity = async () => {
        const identity = await resolveCustomerIdentity();
        setResolvedIdentity({ ...identity, loading: false });
        console.log("[PricingTable] Resolved customer identity:", { 
          phone: identity.phone ? "present" : "missing",
          authPhone: identity.authPhone ? "present" : "missing",
          profilePhone: identity.profilePhone ? "present" : "missing",
          profileCountry: identity.profileCountry || "missing",
          email: identity.email ? "present" : "missing",
          name: identity.name ? "present" : "missing"
        });
      };
      
      fetchIdentity();
    } else {
      setResolvedIdentity({ phone: null, authPhone: null, profilePhone: null, profileCountry: null, email: null, name: null, loading: false });
    }
  }, [userIsAuthenticated, session?.user?.id, session?.user?.phoneNumber, session?.user?.email, session?.user?.name]);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingPaymentSelection, setPendingPaymentSelection] = useState(null);

  const handleChoosePlan = async (planId) => {
    console.log("[PricingTable] handleChoosePlan called:", planId);
    // If user is not authenticated and context is public, redirect to signup
    if (!userIsAuthenticated && context === "public") {
      console.log("[PricingTable] Not authenticated in public context, redirecting to signup");
      router.push("/signup");
      return;
    }

    // Store the selected plan, then collect the payment method so the final
    // confirmation can show the correct renewal terms for that provider.
    const plan =
      planId === INTERVIEW_COPILOT_ONLY_PLAN_ID
        ? copilotOnlyPlan
        : plans.find((p) => p.id === planId);
    if (!plan) return;
    console.log("[PricingTable] Found plan:", plan.name, "| Opening payment method modal");
    setSelectedPlan({
      id: planId,
      name: plan.name || planId,
      prices: plan.prices || {},
      ...(planId === INTERVIEW_COPILOT_ONLY_PLAN_ID
        ? {
            interviewHoursPerMonth: plan.interviewHoursPerMonth,
            interviewMonthlyLimitMinutes:
              plan.interviewMonthlyLimitMinutes,
          }
        : {}),
    });
    setPendingPaymentSelection(null);
    setShowPaymentModal(true);
  };

  const handlePaymentMethodChosen = (gateway, paymentCountry, flutterwaveMethod) => {
    if (!selectedPlan) return;
    if (paymentCountry) setSelectedCountry(paymentCountry);
    setShowPaymentModal(false);
    setPendingPaymentSelection({ gateway, paymentCountry, flutterwaveMethod });
    setShowConfirmModal(true);
  };

  const handleConfirmAndProceed = () => {
    if (!pendingPaymentSelection) return;
    console.log("[PricingTable] Confirmation accepted, proceeding to checkout");
    const { gateway, paymentCountry, flutterwaveMethod } = pendingPaymentSelection;
    setShowConfirmModal(false);
    setPendingPaymentSelection(null);
    handlePaymentMethodSelected(gateway, paymentCountry, flutterwaveMethod);
  };

  const handlePaymentMethodSelected = async (gateway, paymentCountry, flutterwaveMethod) => {
    if (!selectedPlan) return;

    setShowPaymentModal(false);
    setLoading(selectedPlan.id);
    
    // Store selected country for later use
    if (paymentCountry) {
      setSelectedCountry(paymentCountry);
    }

    // Reset local UI state when starting a new payment
    if (gateway === "flutterwave") {
      lastFlutterwaveAttemptRef.current = { gateway, paymentCountry, flutterwaveMethod };
      setShowSuccess(false);
      setBankTransferData(null);
      setFlutterwaveError(null);
      setFlutterwaveErrorCategory(null);
      setProfileError(null);
      setBlacklistError(null);
      setNetworkSelectionData(null);
      setSubCountrySelectionData(null);
      setShowFlutterwaveLoading(true); // Show loading overlay immediately
    }

    try {
      // Determine which API to call based on gateway
      if (gateway === "flutterwave") {
        // Ensure country is selected for Flutterwave payments
        if (!paymentCountry || !paymentCountry.code) {
          console.error("[PricingTable] No country selected for Flutterwave payment");
          setClassifiedFlutterwaveError(tp.select_country_desc, "customer_error");
          setShowFlutterwaveLoading(false);
          setLoading(null);
          return;
        }

        // Ghana can use either MoMo or Bank Transfer.
        // Kenya can use either MoMo or Bank Transfer.
        // Nigeria can use either Bank Transfer or OPay.
        // For any country where the user explicitly selected bank_transfer, route to bank-transfer API.
        if (flutterwaveMethod === "bank_transfer") {
          const response = await fetch("/api/flutterwave/create-bank-transfer", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              uid: session?.user?.id,
              plan: selectedPlan.id,
              interval: safeDuration,
              interviewHoursPerMonth: selectedPlan?.interviewHoursPerMonth,
            }),
          });

          // Safely parse JSON response
          let data = null;
          const countryCurrency = (() => {
            const cfg = getCountryPaymentConfig(paymentCountry.code);
            return cfg?.currency || "NGN";
          })();
          try {
            data = await response.json();
          } catch (parseError) {
            console.error("[PricingTable] Failed to parse Flutterwave response:", parseError);
            setBlacklistError({
              message: t.alert_error,
              supportEmail: SUPPORT_EMAIL,
            });
            setBankTransferData({
              isBlacklisted: true,
              bankName: "",
              accountNumber: "",
              amount: 0,
              currency: countryCurrency,
              tx_ref: "",
            });
            setShowFlutterwaveLoading(false);
            setLoading(null);
            return;
          }

          if (!response.ok) {
            console.error("[PricingTable] Flutterwave bank transfer error:", data.error || data.message);
            if (data.errorCode === "CUSTOMER_PROFILE_INCOMPLETE") {
              setProfileError({
                errorCode: data.errorCode,
                missingFields: data.missingFields || [],
                message: data.message || tp.complete_profile_desc,
              });
              setShowFlutterwaveLoading(false);
            } else if (data.errorCode === "EMAIL_BLACKLISTED") {
              setBlacklistError({
                message: data.message || ERROR_MESSAGES.EMAIL_BLACKLISTED,
                supportEmail: data.supportEmail || SUPPORT_EMAIL,
              });
              setBankTransferData({
                isBlacklisted: true,
                bankName: "",
                accountNumber: "",
                amount: 0,
                currency: countryCurrency,
                tx_ref: "",
              });
              setShowFlutterwaveLoading(false);
            } else if (data.errorCode === "FLW_BANK_TRANSFER_NOT_ENABLED" || data.errorCode === "FLW_GH_BANK_TRANSFER_NOT_ENABLED") {
              setBlacklistError({
                message: data.message || tp.flw_bank_unavailable,
                supportEmail: SUPPORT_EMAIL,
              });
              setBankTransferData({
                isBlacklisted: true,
                bankName: "",
                accountNumber: "",
                amount: 0,
                currency: countryCurrency,
                tx_ref: "",
              });
              setShowFlutterwaveLoading(false);
            } else if (data.errorCode === "FLW_ERROR") {
              setBlacklistError({
                message: data.message || tp.couldnt_process,
                supportEmail: SUPPORT_EMAIL,
              });
              setBankTransferData({
                isBlacklisted: true,
                bankName: "",
                accountNumber: "",
                amount: 0,
                currency: countryCurrency,
                tx_ref: "",
              });
              setShowFlutterwaveLoading(false);
            } else if (data.errorCode === "NON_NIGERIA_PHONE") {
              setShowFlutterwaveLoading(false);
              setShowNigeriaPhonePrompt(true);
            } else {
              setClassifiedFlutterwaveError(
                data.message || tp.couldnt_process,
                data.errorCategory || null
              );
              setShowFlutterwaveLoading(false);
            }

            setLoading(null);
            return;
          }

          setShowFlutterwaveLoading(false);
          setLoading(null);
          if (data.accountNumber && data.bankName) {
            setBankTransferData({ ...data, countryCode: paymentCountry.code });
          }
          return;
        }
        
        // Nigeria OPay: user selected "opay" in the method selector
        if (paymentCountry.code === "NG" && flutterwaveMethod === "opay") {
          // Delegate to handleSwitchToOpay which handles phone validation and API call
          setShowFlutterwaveLoading(false);
          setLoading(null);
          await handleSwitchToOpay();
          return;
        }

        // Check if country supports MoMo and user chose MoMo (or country only has MoMo)
        const countryHasMomo = isMethodEnabled(paymentCountry.code, PAYMENT_METHOD.MOMO);
        if (countryHasMomo && (flutterwaveMethod === "momo" || !flutterwaveMethod)) {
          // Flatten Francophone countries: map BF/CI/SN/CM → XOF/XAF region code
          const rawCountryCode = paymentCountry.code;
          const francophoneRegion = FRANCOPHONE_COUNTRY_TO_REGION[rawCountryCode];
          const momoCountryCode = francophoneRegion || rawCountryCode;
          // If the user selected a flattened Francophone country, we already know the sub-country
          const autoSubCountry = francophoneRegion ? rawCountryCode : null;

          const isGhana = momoCountryCode === "GH";
          const isKenya = momoCountryCode === "KE";
          const isUganda = momoCountryCode === "UG";
          const isZambia = momoCountryCode === "ZM";
          const isTanzania = momoCountryCode === "TZ";
          const isXOF = momoCountryCode === "XOF";
          const isXAF = momoCountryCode === "XAF";
          const isFrancophone = isXOF || isXAF;

          // Effective sub-country: either from direct country selection or from legacy sub-country modal
          const effectiveSubCountry = autoSubCountry || subCountrySelectionData?.selectedSubCountry || null;

          // ── DEV-ONLY diagnostics for Francophone MoMo flow ──
          if (process.env.NODE_ENV === "development") {
            console.log(
              `[momo-diag] rawCountryCode=${rawCountryCode}, francophoneRegion=${francophoneRegion || "none"}, ` +
              `momoCountryCode=${momoCountryCode}, isFrancophone=${isFrancophone}, ` +
              `effectiveSubCountry=${effectiveSubCountry || "NONE"}`
            );
          }

          // For XOF/XAF: require sub-country selection (only triggers if user somehow selected XOF/XAF directly)
          if (isFrancophone && !effectiveSubCountry) {
            console.log(`[ui-${momoCountryCode.toLowerCase()}-momo] Sub-country not selected, showing sub-country selection modal`);
            const subCountries = getSubCountries(momoCountryCode);
            setSubCountrySelectionData({
              subCountries,
              regionCode: momoCountryCode,
              regionName: isXOF ? tp.francophone_west_africa : tp.francophone_central_africa,
              retryPayload: {
                uid: session?.user?.id,
                plan: selectedPlan.id,
                interval: safeDuration,
                interviewHoursPerMonth: selectedPlan?.interviewHoursPerMonth,
              },
            });
            setShowFlutterwaveLoading(false);
            setLoading(null);
            return;
          }

          // Phone validation: for XOF/XAF, validate against the selected sub-country prefix
          {
            const phoneValidationCountry = isFrancophone
              ? (effectiveSubCountry || "").toUpperCase()
              : momoCountryCode;
            const normalizedPhone = normalizePhoneForValidation(resolvedIdentity.phone, phoneValidationCountry);
            console.info(`[ui-${momoCountryCode.toLowerCase()}-momo] Checking phone for MoMo:`, { raw: resolvedIdentity.phone, normalized: normalizedPhone, phoneValidationCountry });
            
            // Client-side validation by country BEFORE calling backend
            const phoneMatchesCountry = phoneMatchesMomoCountry(normalizedPhone, phoneValidationCountry);
            const validationCountryName = COUNTRY_CODE_TO_NAME[phoneValidationCountry] || phoneValidationCountry;
            const profileCountryOk = profileCountryMatchesRequired(resolvedIdentity.profileCountry, phoneValidationCountry, validationCountryName);
            if (!normalizedPhone || !phoneMatchesCountry || !profileCountryOk) {
              console.log(`[ui-${momoCountryCode.toLowerCase()}-momo] Phone or profile country mismatch, showing prompt:`, normalizedPhone || "missing", "profileCountry:", resolvedIdentity.profileCountry || "missing", "profileCountryOk:", profileCountryOk);
              if (isFrancophone) {
                setFrancophonePhoneSubCountry(phoneValidationCountry);
                setShowFrancophonePhonePrompt(true);
              } else if (isGhana) {
                setShowGhanaPhonePrompt(true);
              } else if (isKenya) {
                setShowKenyaPhonePrompt(true);
              } else if (isUganda) {
                setShowUgandaPhonePrompt(true);
              } else if (isZambia) {
                setShowZambiaPhonePrompt(true);
              } else if (momoCountryCode === "RW") {
                setShowRwandaPhonePrompt(true);
              } else if (momoCountryCode === "ZA") {
                setShowSouthAfricaPhonePrompt(true);
              } else if (momoCountryCode === "EG") {
                setShowEgyptPhonePrompt(true);
              } else {
                setShowTanzaniaPhonePrompt(true);
              }
              setShowFlutterwaveLoading(false);
              setLoading(null);
              return; // Don't proceed to backend
            }
            
            console.info(`[ui-${momoCountryCode.toLowerCase()}-momo] Country phone validated`);
          }
          
          // For countries with network selection: GH, RW, UG, ZM, TZ, XOF, XAF
          const requiresNetworkSelection = isGhana || momoCountryCode === "RW" || isUganda || isZambia || isTanzania || isFrancophone;
          if (requiresNetworkSelection && (!networkSelectionData || !networkSelectionData.selectedNetwork)) {
            console.log(`[ui-${momoCountryCode.toLowerCase()}-momo] Network not selected, showing network selection modal`);
            
            // For Francophone regions, use the sub-country operators
            let supportedNetworks;
            if (isFrancophone && effectiveSubCountry) {
              supportedNetworks = getSubCountryOperators(momoCountryCode, effectiveSubCountry);
              if (process.env.NODE_ENV === "development") {
                console.log(
                  `[momo-diag] getSubCountryOperators(${momoCountryCode}, ${effectiveSubCountry}) → ` +
                  `${supportedNetworks.length} operators: [${supportedNetworks.map(n => n.key).join(", ")}]`
                );
                if (!supportedNetworks.length) {
                  console.warn(
                    `[momo-diag] ⚠ EMPTY operators for ${effectiveSubCountry} in ${momoCountryCode}! ` +
                    `Check momo_shared.ts sub-country data.`
                  );
                }
              }
            } else if (isTanzania) {
              supportedNetworks = [
                { key: "airtel", label: "Airtel" },
                { key: "tigo", label: "Tigo" },
                { key: "halopesa", label: "Halopesa" },
              ];
            } else if (momoCountryCode === "RW") {
              supportedNetworks = [
                { key: "mtn", label: "MTN" },
                { key: "airtel", label: "Airtel Money" },
              ];
            } else if (isUganda) {
              supportedNetworks = [
                { key: "mtn", label: "MTN" },
                { key: "airtel", label: "Airtel Money" },
              ];
            } else if (isZambia) {
              supportedNetworks = [
                { key: "mtn", label: "MTN" },
                { key: "airtel", label: "Airtel Money" },
                { key: "zamtel", label: "Zamtel" },
              ];
            } else {
              // Ghana
              supportedNetworks = [
                { key: "mtn", label: "MTN" },
                { key: "vodafone", label: "Vodafone" },
                { key: "tigo", label: "Airtel" },
              ];
            }

            // ── Safety fallback: abort if no providers could be loaded ──
            if (!supportedNetworks || supportedNetworks.length === 0) {
              console.error(
                `[momo-diag] No supported networks resolved for country=${rawCountryCode}, ` +
                `region=${momoCountryCode}, sub=${effectiveSubCountry}. Blocking payment.`
              );
              setClassifiedFlutterwaveError(
                tp.providers_unavailable,
                "gateway_error"
              );
              setShowFlutterwaveLoading(true); // keep overlay so error renders
              setLoading(null);
              return;
            }

            const normalizedPhone = isFrancophone
              ? (resolvedIdentity.phone || "")
              : normalizePhoneForValidation(resolvedIdentity.phone, momoCountryCode);

            setNetworkSelectionData({
              supportedNetworks,
              countryCode: momoCountryCode,
              subCountryCode: isFrancophone ? effectiveSubCountry : undefined,
              message: tp.select_mobile_provider,
              retryPayload: {
                uid: session?.user?.id,
                plan: selectedPlan.id,
                interval: safeDuration,
                interviewHoursPerMonth: selectedPlan?.interviewHoursPerMonth,
                phoneNumber: normalizedPhone,
                countryCode: momoCountryCode,
              }
            });
            setShowFlutterwaveLoading(false);
            setLoading(null);
            return; // Don't proceed to backend without network
          }
          
          console.info(`[ui-${momoCountryCode.toLowerCase()}-momo] Proceeding to backend`);
          
          // ── Guard: Francophone must always have a network before hitting backend ──
          if (isFrancophone && !networkSelectionData?.selectedNetwork) {
            console.error(
              `[momo-diag] Francophone (${momoCountryCode}, sub=${effectiveSubCountry}) ` +
              `reached backend call without a selected network. Blocking.`
            );
            setClassifiedFlutterwaveError(
              tp.providers_unavailable,
              "gateway_error"
            );
            setShowFlutterwaveLoading(true);
            setLoading(null);
            return;
          }

          // Call MoMo API; Ghana uses selected network, Kenya defaults to mps in backend
          const isFrancophoneRegion = momoCountryCode === "XOF" || momoCountryCode === "XAF";
          const normalizedPhoneForApi = isFrancophoneRegion
            ? (resolvedIdentity.phone || "")
            : normalizePhoneForValidation(resolvedIdentity.phone, momoCountryCode);

          const requestBody = {
            uid: session?.user?.id,
            plan: selectedPlan.id,
            interval: safeDuration,
            interviewHoursPerMonth: selectedPlan?.interviewHoursPerMonth,
            phoneNumber: normalizedPhoneForApi,
            countryCode: momoCountryCode,
            ...(networkSelectionData?.selectedNetwork
              ? { network: networkSelectionData.selectedNetwork }
              : {}),
            ...(isFrancophoneRegion && effectiveSubCountry
              ? { subCountryCode: effectiveSubCountry }
              : {}),
          };

          const response = await fetch("/api/flutterwave/create-momo-charge", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
          });

          const data = await response.json();

          if (!response.ok) {
            console.error("[PricingTable] MoMo charge error:", data.error || data.message);
            
            // Handle NON_GHANA_PHONE error from backend
            if (data.errorCode === "NON_GHANA_PHONE") {
              setShowGhanaPhonePrompt(true);
            } else if (data.errorCode === "NON_KENYA_PHONE") {
              setShowKenyaPhonePrompt(true);
            } else if (data.errorCode === "NON_RWANDA_PHONE") {
              setShowRwandaPhonePrompt(true);
            } else if (data.errorCode === "NON_TANZANIA_PHONE") {
              setShowTanzaniaPhonePrompt(true);
            } else if (data.errorCode === "NON_UGANDA_PHONE") {
              setShowUgandaPhonePrompt(true);
            } else if (data.errorCode === "NON_ZAMBIA_PHONE") {
              setShowZambiaPhonePrompt(true);
            } else if (data.errorCode === "NON_SOUTH_AFRICA_PHONE") {
              setShowSouthAfricaPhonePrompt(true);
            } else if (data.errorCode === "NON_EGYPT_PHONE") {
              setShowEgyptPhonePrompt(true);
            } else if (data.errorCode === "CUSTOMER_PROFILE_INCOMPLETE") {
              setProfileError({
                errorCode: data.errorCode,
                missingFields: data.missingFields || [],
                message: data.message || tp.complete_profile_desc
              });
            } else if (data.errorCode === "NETWORK_REQUIRED") {
              // Show network selection modal with supported networks
              console.log("[PricingTable] Network required, showing selection modal:", data.supportedNetworks);
              setNetworkSelectionData({
                supportedNetworks: data.supportedNetworks || [],
                countryCode: data.countryCode || "GH",
                message: data.message || tp.select_mobile_provider,
                // Store the request payload to retry with network
                retryPayload: {
                  uid: session?.user?.id,
                  plan: selectedPlan.id,
                  interval: safeDuration,
                  interviewHoursPerMonth: selectedPlan?.interviewHoursPerMonth,
                  phoneNumber: normalizedPhoneForApi,
                  countryCode: data.countryCode || momoCountryCode,
                }
              });
            } else if (
              data.errorCode === "PAYMENT_METHOD_NOT_ENABLED" ||
              data.errorCode === "PAYMENT_UNDER_MAINTENANCE" ||
              data.errorCode === "PAYMENT_GATEWAY_UNREACHABLE" ||
              data.errorCode === "PAYMENT_GATEWAY_AUTH_ERROR"
            ) {
              // Graceful "MoMo unavailable" message — guide user to Stripe
              console.warn(`[PricingTable] MoMo service error: ${data.errorCode}`);
              setClassifiedFlutterwaveError(
                data.message ||
                tp.providers_unavailable,
                "gateway_error"
              );
            } else if (data.errorCode === "CHARGE_FAILED") {
              // Provider declined — surface the classified message
              console.warn("[PricingTable] MoMo charge failed by provider:", data.message);
              setClassifiedFlutterwaveError(
                data.message ||
                tp.couldnt_process,
                data.errorCategory || null
              );
            } else {
              setClassifiedFlutterwaveError(
                data.message || data.error || t.alert_error,
                data.errorCategory || null
              );
            }

            // If we're showing an error message, keep the overlay visible so it can render.
            // (Network selection + phone prompts intentionally close the overlay.)
            if (
              data.errorCode !== "NETWORK_REQUIRED" &&
              data.errorCode !== "NON_GHANA_PHONE" &&
              data.errorCode !== "NON_KENYA_PHONE" &&
              data.errorCode !== "NON_RWANDA_PHONE" &&
              data.errorCode !== "NON_TANZANIA_PHONE" &&
              data.errorCode !== "NON_UGANDA_PHONE" &&
              data.errorCode !== "NON_ZAMBIA_PHONE" &&
              data.errorCode !== "NON_SOUTH_AFRICA_PHONE" &&
              data.errorCode !== "NON_EGYPT_PHONE"
            ) {
              setShowFlutterwaveLoading(true);
            } else {
              setShowFlutterwaveLoading(false);
            }
            setLoading(null);
            return;
          }

          // If successful, hide loading overlay and show mobile wallet details modal
          setShowFlutterwaveLoading(false);
          setLoading(null);
          // Show modal for:
          // 1. OPay redirect (has redirect_url/payment_url)
          // 2. MoMo pending (push/prompt flows often return pending without a URL/instructions)
          const hasRedirectUrl = Boolean(data.redirect_url || data.payment_url);
          const isPending = data.status === "pending";

          // Provide a fallback instruction for MoMo push/prompt flows
          // so the modal can render and auto-poll without needing a payment_url.
          if (isPending && !hasRedirectUrl && !data.instructions) {
            data.instructions = `${tp.approve_momo_prompt}. ${tp.keep_open_60s}`;
          }

          if (hasRedirectUrl || isPending || data.authorizationRedirect) {
            const walletData = { ...data };
            const countryWalletTypeMap = {
              GH: WALLET_TYPE_GHANA_MOMO,
              KE: WALLET_TYPE_KENYA_MOMO,
              RW: WALLET_TYPE_RWANDA_MOMO,
              TZ: WALLET_TYPE_TANZANIA_MOMO,
              UG: WALLET_TYPE_UGANDA_MOMO,
              ZM: WALLET_TYPE_ZAMBIA_MOMO,
              XOF: WALLET_TYPE_XOF_MOMO,
              XAF: WALLET_TYPE_XAF_MOMO,
            };
            const resolvedWalletType = countryWalletTypeMap[momoCountryCode] || WALLET_TYPE_GHANA_MOMO;
            setMobileWalletData(walletData);
            setWalletType(resolvedWalletType);
            // Persist to localStorage so polling resumes after refresh
            savePendingMomoPayment(walletData, resolvedWalletType, momoCountryCode);
          }
          
          return; // Exit early, don't call bank transfer API
        }
        
        // Fallthrough: countries without an explicit method from the modal
        // (shouldn't normally reach here if config-driven routing is correct,
        //  but kept as safety net for backward compat with old flows)
        if (paymentCountry.code === "NG") {
          const normalizedPhone = normalizePhoneForValidation(resolvedIdentity.phone, "NG");
          const profileCountryOk = profileCountryMatchesRequired(resolvedIdentity.profileCountry, "NG", "Nigeria");
          console.info("[ui-ng-bank-transfer] Checking phone + profile country for Nigeria bank transfer:", {
            raw: resolvedIdentity.phone,
            normalized: normalizedPhone,
            profileCountry: resolvedIdentity.profileCountry,
            profileCountryOk,
          });

          if (!normalizedPhone || !normalizedPhone.startsWith("+234") || !profileCountryOk) {
            console.log(
              "[ui-ng-bank-transfer] Phone or profile country mismatch, showing prompt:",
              normalizedPhone || "missing", "profileCountry:", resolvedIdentity.profileCountry || "missing"
            );
            setShowFlutterwaveLoading(false);
            setLoading(null);
            setShowNigeriaPhonePrompt(true);
            return;
          }
        }

        {
        const fallbackCurrency = (() => {
          const cfg = getCountryPaymentConfig(paymentCountry.code);
          return cfg?.currency || "NGN";
        })();

        const response = await fetch("/api/flutterwave/create-bank-transfer", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            uid: session?.user?.id,
            plan: selectedPlan.id,
            interval: safeDuration,
            interviewHoursPerMonth: selectedPlan?.interviewHoursPerMonth,
          }),
        });

        // Safely parse JSON response
        let data = null;
        try {
          data = await response.json();
        } catch (parseError) {
          console.error("[PricingTable] Failed to parse Flutterwave response:", parseError);
          // Show generic error in modal instead of closing it
          setBlacklistError({
            message: t.alert_error,
            supportEmail: SUPPORT_EMAIL
          });
          setBankTransferData({
            isBlacklisted: true,
            bankName: "",
            accountNumber: "",
            amount: 0,
            currency: fallbackCurrency,
            tx_ref: "",
          });
          setShowFlutterwaveLoading(false);
          return;
        }

        if (!response.ok) {
          console.error("[PricingTable] Flutterwave bank transfer error:", data.error || data.message);
          
          // Handle structured errors from backend
          if (data.errorCode === "CUSTOMER_PROFILE_INCOMPLETE") {
            setProfileError({
              errorCode: data.errorCode,
              missingFields: data.missingFields || [],
              message: data.message || tp.complete_profile_desc
            });
            setShowFlutterwaveLoading(false);
          } else if (data.errorCode === "EMAIL_BLACKLISTED") {
            setBlacklistError({
              message: data.message || ERROR_MESSAGES.EMAIL_BLACKLISTED,
              supportEmail: data.supportEmail || SUPPORT_EMAIL
            });
            setBankTransferData({
              isBlacklisted: true,
              bankName: "",
              accountNumber: "",
              amount: 0,
              currency: fallbackCurrency,
              tx_ref: "",
            });
            setShowFlutterwaveLoading(false);
          } else if (data.errorCode === "FLW_ERROR") {
            setBlacklistError({
              message: data.message || tp.couldnt_process,
              supportEmail: SUPPORT_EMAIL
            });
            setBankTransferData({
              isBlacklisted: true,
              bankName: "",
              accountNumber: "",
              amount: 0,
              currency: fallbackCurrency,
              tx_ref: "",
            });
            setShowFlutterwaveLoading(false);
          } else if (data.errorCode === "FLW_BANK_TRANSFER_NOT_ENABLED" || data.errorCode === "FLW_GH_BANK_TRANSFER_NOT_ENABLED") {
            setBlacklistError({
              message: data.message || tp.flw_bank_unavailable,
              supportEmail: SUPPORT_EMAIL,
            });
            setBankTransferData({
              isBlacklisted: true,
              bankName: "",
              accountNumber: "",
              amount: 0,
              currency: fallbackCurrency,
              tx_ref: "",
            });
            setShowFlutterwaveLoading(false);
          } else if (data.errorCode === "NON_NIGERIA_PHONE") {
            // Nigeria bank transfer requires +234 (guardrail)
            setShowFlutterwaveLoading(false);
            setShowNigeriaPhonePrompt(true);
          } else {
            // Fallback for unstructured errors
            setClassifiedFlutterwaveError(
              data.message || data.error || t.alert_error,
              data.errorCategory || null
            );
            setShowFlutterwaveLoading(false);
          }
          
          return;
        }

        // If successful, hide loading overlay and show bank transfer details modal
        setShowFlutterwaveLoading(false);
        if (data.accountNumber && data.bankName) {
          setBankTransferData({ ...data, countryCode: paymentCountry.code });
          // Note: Do NOT set showSuccess here - only after payment is confirmed
        }
        } // end block-scoped fallback
      } else {
        await handlePlanCheckout({
          planId: selectedPlan.id,
          interval: safeDuration,
          interviewHoursPerMonth: selectedPlan.interviewHoursPerMonth,
          language: apiLanguage,
        });
      }
    } catch (error) {
      console.error("[PricingTable] Error:", error);
      if (gateway === "flutterwave") {
        setClassifiedFlutterwaveError(t.alert_error, "gateway_error");
        setShowFlutterwaveLoading(true);
      } else {
        alert(t.alert_error);
      }
    } finally {
      setLoading(null);
    }
  };

  // Retry Flutterwave payment on error
  const handleFlutterwaveRetry = () => {
    setFlutterwaveError(null);
    setFlutterwaveErrorCategory(null);
    setShowFlutterwaveLoading(false);
    // Replay the last attempted payment instead of reopening the method selector
    const lastAttempt = lastFlutterwaveAttemptRef.current;
    if (lastAttempt) {
      handlePaymentMethodSelected(lastAttempt.gateway, lastAttempt.paymentCountry, lastAttempt.flutterwaveMethod);
    } else {
      // Fallback: reopen payment modal if no prior attempt recorded
      setShowPaymentModal(true);
    }
  };

  // Switch from bank transfer to Stripe checkout
  const handleSwitchToStripe = () => {
    if (!selectedPlan) {
      console.warn("[PricingTable] No selected plan for Stripe switch");
      return;
    }

    setShowFlutterwaveLoading(false);
    setFlutterwaveError(null);
    setFlutterwaveErrorCategory(null);
    handlePaymentMethodChosen("stripe");
  };

  // Switch from bank transfer to OPay payment
  const handleSwitchToOpay = async () => {
    if (!selectedPlan) {
      console.warn("[PricingTable] No selected plan for OPay switch");
      return;
    }
    
    // Use resolved identity phone (single source of truth)
    const normalizedPhone = normalizePhoneForValidation(resolvedIdentity.phone, "NG");
    const profileCountryOk = profileCountryMatchesRequired(resolvedIdentity.profileCountry, "NG", "Nigeria");
    console.info("[ui-opay] Checking phone + profile country for OPay:", { raw: resolvedIdentity.phone, normalized: normalizedPhone, profileCountry: resolvedIdentity.profileCountry, profileCountryOk });
    
    // Client-side validation - check for Nigerian phone AND profile country BEFORE calling backend
    if (!normalizedPhone || !normalizedPhone.startsWith("+234") || !profileCountryOk) {
      console.log("[ui-opay] Phone or profile country mismatch, showing prompt:", normalizedPhone || "missing", "profileCountry:", resolvedIdentity.profileCountry || "missing");
      setShowNigeriaPhonePrompt(true);
      return; // Don't proceed to backend
    }
    
    console.info("[ui-opay] Nigerian phone validated, proceeding to backend");
    setLoading(selectedPlan.id);
    setShowFlutterwaveLoading(true); // Show loading overlay
    
    try {
      const response = await fetch("/api/flutterwave/create-opay-charge", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          uid: session?.user?.id,
          plan: selectedPlan.id,
          interval: safeDuration,
          interviewHoursPerMonth: selectedPlan?.interviewHoursPerMonth,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error("[PricingTable] OPay charge error:", data.error || data.message);
        
        // Handle NON_NIGERIA_PHONE error from backend
        if (data.errorCode === "NON_NIGERIA_PHONE") {
          setShowNigeriaPhonePrompt(true);
        } else {
          setClassifiedFlutterwaveError(
            data.message || data.error || t.alert_error,
            data.errorCategory || null
          );
        }
        
        setShowFlutterwaveLoading(false);
        return;
      }

      // If successful, hide loading overlay and show OPay details modal
      setShowFlutterwaveLoading(false);
      if (data.payment_url || data.paymentUrl) {
        setMobileWalletData(data);
        setWalletType(WALLET_TYPE_OPAY);
      }
    } catch (error) {
      console.error("[PricingTable] Error switching to OPay:", error);
      setClassifiedFlutterwaveError("An error occurred. Please try again.", "gateway_error");
      setShowFlutterwaveLoading(false);
    } finally {
      setLoading(null);
    }
  };

  // Switch to MoMo payment (from bank transfer or other method)
  const handleSwitchToMomo = () => {
    if (!selectedPlan || !selectedCountry) return;
    // Clear current payment state
    setBankTransferData(null);
    setMobileWalletData(null);
    setWalletType(null);
    setBlacklistError(null);
    setFlutterwaveError(null);
    setFlutterwaveErrorCategory(null);
    setShowFlutterwaveLoading(false);
    setNetworkSelectionData(null);
    setSubCountrySelectionData(null);
    // Re-enter payment flow with MoMo method
    handlePaymentMethodSelected("flutterwave", selectedCountry, "momo");
  };

  // Switch to Bank Transfer payment (from MoMo or other method)
  const handleSwitchToBankTransfer = () => {
    if (!selectedPlan || !selectedCountry) return;
    // Clear current payment state
    setBankTransferData(null);
    setMobileWalletData(null);
    setWalletType(null);
    setNetworkSelectionData(null);
    setSubCountrySelectionData(null);
    setBlacklistError(null);
    setFlutterwaveError(null);
    setFlutterwaveErrorCategory(null);
    setShowFlutterwaveLoading(false);
    // Re-enter payment flow with bank_transfer method
    handlePaymentMethodSelected("flutterwave", selectedCountry, "bank_transfer");
  };

  // Get plans from JSON and use explicit prices
  const plans = useMemo(() => {
    return pricingData.plans.map(plan => {
      const planId = plan.id;
      
      // Get explicit prices directly from config
      const anchorPrices = pricingData.anchorPrices[planId];
      const promoPrices = pricingData.promoPrices[planId];

      if (!anchorPrices || !promoPrices) {
        console.error(`[PricingTable] Missing prices for plan: ${planId}`);
        return {
          ...plan,
          prices: {
            monthly: { final: 0, original: 0 },
            quarterly: { final: 0, original: 0 },
            semiannual: { final: 0, original: 0 },
          }
        };
      }

      const prices = {
        monthly: { 
          final: promoPrices.monthly, 
          original: anchorPrices.monthly 
        },
        quarterly: { 
          final: promoPrices.quarterly, 
          original: anchorPrices.quarterly 
        },
        semiannual: { 
          final: promoPrices.semiannual, 
          original: anchorPrices.semiannual 
        },
      };

      return {
        ...plan,
        ...premium.plans[planId],
        prices
      };
    });
  }, [premium]);

  const copilotOnlyPlan = useMemo(() => {
    const monthly = calculateInterviewCopilotOnlyPrice(
      "monthly",
      interviewHoursPerMonth
    );
    const quarterly = calculateInterviewCopilotOnlyPrice(
      "quarterly",
      interviewHoursPerMonth
    );
    const semiannual = calculateInterviewCopilotOnlyPrice(
      "semiannual",
      interviewHoursPerMonth
    );

    return {
      id: INTERVIEW_COPILOT_ONLY_PLAN_ID,
      name: premium.interviewCopilotOnly.name,
      interviewHoursPerMonth,
      interviewMonthlyLimitMinutes: interviewHoursPerMonth * 60,
      prices: {
        monthly: {
          final: monthly.subtotalUsd,
          original: monthly.regularSubtotalUsd,
          details: monthly,
        },
        quarterly: {
          final: quarterly.subtotalUsd,
          original: quarterly.regularSubtotalUsd,
          details: quarterly,
        },
        semiannual: {
          final: semiannual.subtotalUsd,
          original: semiannual.regularSubtotalUsd,
          details: semiannual,
        },
      },
    };
  }, [interviewHoursPerMonth, premium.interviewCopilotOnly.name]);

  const copilotPriceForDuration =
    copilotOnlyPlan.prices[safeDuration];

  return (
    <section id="pricing" className="relative mx-auto max-w-[1600px] scroll-mt-20 overflow-hidden bg-[radial-gradient(circle_at_top_left,_#ede9fe_0,_transparent_35%),linear-gradient(180deg,#fbfaff_0%,#f5f3ff_48%,#fafafa_100%)] px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
      <div aria-hidden="true" className="pointer-events-none absolute -left-28 top-44 h-72 w-72 rounded-full bg-violet-300/20 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -right-24 top-10 h-80 w-80 rounded-full bg-amber-200/20 blur-3xl" />

      <div className="relative mx-auto max-w-4xl text-center">
        <div className="flex flex-wrap items-center justify-center gap-3"><DiscountBadge copy={premium} /><span className="text-xs font-black uppercase tracking-[0.2em] text-violet-700">{premium.page.eyebrow}</span></div>
        <h2 className="mt-5 text-3xl font-black tracking-[-0.035em] text-slate-950 sm:text-5xl lg:text-6xl">{premium.page.title}</h2>
        <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">{premium.page.description}</p>
        <p className="mt-4 text-sm font-bold text-slate-700">{premium.page.promoLine.split("{code}")[0]}<span className="rounded-lg border border-violet-200 bg-white px-2.5 py-1 font-black tracking-wide text-[#5A4AF4] shadow-sm">JOBOLAR60OFF</span>{premium.page.promoLine.split("{code}")[1]}</p>
      </div>

      <div role="tablist" aria-label={premium.page.billingIntervalAria} className="relative mx-auto mt-8 grid w-full max-w-lg grid-cols-3 items-center gap-1 rounded-2xl border border-white/90 bg-white/80 p-1.5 shadow-[0_12px_35px_rgba(45,32,92,0.12)] backdrop-blur">
        {durations.map((d, index) => (
          <button key={d.key} type="button" role="tab" data-billing-tab={d.key} aria-selected={safeDuration === d.key} tabIndex={safeDuration === d.key ? 0 : -1} onClick={() => setDuration(normalizeDuration(d.key))} onKeyDown={(event) => handleDurationKeyDown(event, index)} className={`min-w-0 rounded-xl border px-2 py-2.5 text-xs font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5A4AF4] focus-visible:ring-offset-2 sm:px-4 sm:text-sm ${safeDuration === d.key ? "border-[#5A4AF4] bg-[#5A4AF4] text-white shadow-lg shadow-violet-200" : "border-transparent text-slate-600 hover:bg-violet-50 hover:text-violet-800"}`}>{d.label}</button>
        ))}
      </div>

      <article className="relative mx-auto mt-10 max-w-6xl overflow-hidden rounded-[32px] border border-violet-200/80 bg-gradient-to-br from-white via-white to-violet-50 p-6 shadow-[0_24px_80px_rgba(70,50,140,0.16)] sm:p-8">
        <div aria-hidden="true" className="absolute right-0 top-0 h-40 w-40 rounded-bl-full bg-gradient-to-br from-amber-200/60 to-violet-200/50" />
        <div className="relative flex flex-col gap-7 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#241548] px-3 py-1.5 text-xs font-black uppercase tracking-[0.14em] text-amber-200"><WandSparkles size={14} /> {premium.freeTrial.badge}</div>
            <h3 className="mt-4 text-2xl font-black tracking-tight text-slate-950 sm:text-3xl">{premium.freeTrial.headline}</h3>
            <div className="mt-5 grid gap-x-7 gap-y-2.5 text-sm text-slate-700 sm:grid-cols-2 lg:grid-cols-3">
              {premium.freeTrial.features.map((feature) => <div key={feature} className="flex items-start gap-2"><Check className="mt-0.5 shrink-0 text-emerald-600" size={15} /><span>{feature}</span></div>)}
            </div>
          </div>
          <div className="shrink-0 text-center lg:w-56">
            {trialState?.active ? <div className="rounded-2xl bg-emerald-100 px-4 py-3 font-bold text-emerald-800">{formatPricingPremiumText(premium.freeTrial.activeUntil, { date: new Intl.DateTimeFormat(locale).format(new Date(trialState.expiresAt)) })}</div> : trialState && !trialState.enabled ? <div className="rounded-2xl bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-600">{premium.freeTrial.unavailable}</div> : trialState?.everClaimed ? <div className="rounded-2xl bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-600">{premium.freeTrial.alreadyUsed}</div> : <button type="button" onClick={handleStartTrial} disabled={trialLoading} className="w-full rounded-2xl bg-gradient-to-r from-[#5A4AF4] to-[#7667ff] px-5 py-3.5 font-extrabold text-white shadow-xl shadow-violet-200 transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60">{trialLoading ? premium.freeTrial.starting : premium.freeTrial.startCta}</button>}
            <p className="mt-2 text-xs text-slate-500">{premium.freeTrial.footnote}</p>
          </div>
        </div>
        {trialError ? <p className="relative mt-4 rounded-xl bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{trialError}</p> : null}
      </article>

      <div className="relative mx-auto mt-10 grid max-w-6xl grid-cols-1 items-stretch gap-6 lg:grid-cols-3">
        {plans.map((plan) => <LuxuryPlanCard key={plan.id} plan={plan} price={plan.prices[safeDuration]} duration={safeDuration} copy={premium} loading={loading === plan.id} onChoose={() => handleChoosePlan(plan.id)} />)}
      </div>

      <article className="relative mx-auto mt-10 max-w-6xl overflow-hidden rounded-[32px] border border-violet-300/60 bg-gradient-to-br from-[#1d123e] via-[#291553] to-[#17102e] p-6 text-white shadow-[0_28px_90px_rgba(42,24,91,0.32)] sm:p-8">
        <div aria-hidden="true" className="absolute -right-20 -top-24 h-72 w-72 rounded-full bg-violet-400/20 blur-2xl" />
        <div className="relative grid gap-8 lg:grid-cols-[1.2fr_0.8fr] lg:items-start">
          <div>
            <div className="flex flex-wrap items-center gap-3"><span className="rounded-2xl bg-amber-300/15 p-2.5 text-amber-200 ring-1 ring-amber-200/20"><Zap size={20} /></span><span className="text-xs font-black uppercase tracking-[0.18em] text-amber-200">{premium.interviewCopilotOnly.tag}</span><DiscountBadge copy={premium} /></div>
            <h3 className="mt-5 text-3xl font-black tracking-tight">{premium.interviewCopilotOnly.name}</h3>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-violet-100">{premium.interviewCopilotOnly.description}</p>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {premium.interviewCopilotOnly.features.slice(0, 8).map((feature) => <li key={feature} className="flex items-start gap-2.5 text-sm leading-5 text-violet-50"><span className="mt-0.5 rounded-full bg-amber-300/15 p-1 text-amber-200"><Check size={13} /></span><span>{feature}</span></li>)}
            </ul>
            <details className="mt-5 rounded-2xl border border-white/10 bg-white/[0.05]"><summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3 text-sm font-bold text-violet-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300">{premium.labels.copilotDetails} <ChevronDown size={16} /></summary><ul className="space-y-2 border-t border-white/10 px-4 py-4">{premium.interviewCopilotOnly.features.slice(8).map((feature) => <li key={feature} className="flex items-start gap-2 text-xs leading-5 text-violet-100"><Check className="mt-0.5 shrink-0 text-amber-200" size={13} />{feature}</li>)}</ul></details>
          </div>

          <div className="rounded-[24px] border border-white/15 bg-white/[0.09] p-5 backdrop-blur">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-violet-200">{premium.labels.selectHoursPerMonth}</p>
            <div className="mt-4 flex items-center justify-between rounded-2xl bg-white p-2 text-slate-950 shadow-xl">
              <button type="button" aria-label={premium.labels.decreaseHoursAria} onClick={() => updateInterviewHours(interviewHoursPerMonth - 1)} disabled={interviewHoursPerMonth <= INTERVIEW_COPILOT_ONLY_MIN_HOURS} className="rounded-xl p-2.5 text-violet-700 transition hover:bg-violet-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5A4AF4] disabled:cursor-not-allowed disabled:opacity-30"><Minus size={18} /></button>
              <div className="text-center"><input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={3} aria-label={premium.labels.hoursInputAria} value={interviewHoursInput} onChange={handleInterviewHoursInput} onBlur={commitInterviewHoursInput} onKeyDown={(event) => { if (event.key === "Enter") event.currentTarget.blur(); }} className="w-20 rounded-lg border border-violet-200 bg-white px-2 py-1 text-center text-2xl font-black text-slate-950 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-200" /><p className="mt-1 text-xs font-bold text-slate-600">{interviewHoursPerMonth === 1 ? premium.labels.hourPerMonth : premium.labels.hoursPerMonth}</p></div>
              <button type="button" aria-label={premium.labels.increaseHoursAria} onClick={() => updateInterviewHours(interviewHoursPerMonth + 1)} disabled={interviewHoursPerMonth >= INTERVIEW_COPILOT_ONLY_MAX_HOURS} className="rounded-xl p-2.5 text-violet-700 transition hover:bg-violet-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5A4AF4] disabled:cursor-not-allowed disabled:opacity-30"><Plus size={18} /></button>
            </div>
            <div className="mt-5 text-center"><p className="text-4xl font-black tracking-tight">${copilotPriceForDuration.final.toFixed(2)}</p><p className="mt-1 text-sm font-bold text-violet-200">{formatPricingPremiumText(premium.labels.dueForTerm, { term: premium.billing[safeDuration === "monthly" ? "termMonthly" : safeDuration === "quarterly" ? "termQuarterly" : "termSemiannual"] })}</p><p className="mt-2 text-sm text-violet-100"><span className="line-through">${copilotPriceForDuration.original.toFixed(2)}</span> <span className="ml-1 rounded-full bg-amber-300/15 px-2 py-0.5 font-black text-amber-200">{formatPricingPremiumText(premium.page.discountBadge, { percent: Number(pricingData.promoPercent) })}</span></p></div>
            <div className="mt-5 rounded-2xl border border-white/10 bg-black/10 p-4 text-sm text-violet-100"><p className="font-black text-white">{formatPricingPremiumText(premium.labels.monthlyMinutesReset, { minutes: copilotPriceForDuration.details.monthlyMinutes })}</p><p className="mt-1 text-xs leading-5">{formatPricingPremiumText(copilotPriceForDuration.details.termHours === 1 ? premium.labels.prepaidHour : premium.labels.prepaidHours, { hours: copilotPriceForDuration.details.termHours })}</p></div>
            <button type="button" onClick={() => handleChoosePlan(INTERVIEW_COPILOT_ONLY_PLAN_ID)} disabled={loading === INTERVIEW_COPILOT_ONLY_PLAN_ID} className="mt-5 w-full rounded-2xl bg-gradient-to-r from-amber-300 to-yellow-200 px-4 py-3.5 text-sm font-extrabold text-[#291b00] shadow-xl transition hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#291553] disabled:cursor-wait disabled:opacity-60">{loading === INTERVIEW_COPILOT_ONLY_PLAN_ID ? premium.labels.loading : premium.labels.chooseCopilotOnly}</button>
          </div>
        </div>
      </article>

      <PricingFaq copy={premium} locale={locale} />

      <div className="relative mx-auto mt-8 max-w-5xl rounded-2xl border border-violet-100 bg-white/80 p-3 text-center text-xs font-semibold text-slate-600 shadow-sm"><p className="flex items-center justify-center gap-2"><ShieldCheck size={15} className="text-[#5A4AF4]" /> {premium.labels.currencyDisclaimer}</p></div>

      <style jsx>{`@keyframes jobolar-shine { 0%, 58% { transform: translateX(-220%); } 76%, 100% { transform: translateX(520%); } } :global(.jobolar-discount-shine) { animation: jobolar-shine 4.8s ease-in-out infinite; } @media (prefers-reduced-motion: reduce) { :global(.jobolar-discount-shine) { animation: none; transform: none; opacity: 0; } }`}</style>

      {/* Bank Transfer Modal */}
      {bankTransferData && (
        <BankTransferDetails
          bankName={bankTransferData.bankName}
          accountNumber={bankTransferData.accountNumber}
          amountNgn={bankTransferData.amount || bankTransferData.amountNgn}
          currency={bankTransferData.currency}
          countryCode={bankTransferData.countryCode || selectedCountry?.code}
          txRef={bankTransferData.tx_ref || bankTransferData.txRef}
          expiresAt={bankTransferData.expires_at || bankTransferData.expiresAt}
          onComplete={() => {
            setBankTransferData(null);
            setBlacklistError(null);
            setShowSuccess(true);
          }}
          onCancel={() => {
            setBankTransferData(null);
            setBlacklistError(null);
          }}
          onSwitchToStripe={handleSwitchToStripe}
          onSwitchToOpay={selectedCountry?.code === "NG" ? handleSwitchToOpay : undefined}
          onSwitchToMomo={isMethodEnabled(selectedCountry?.code, PAYMENT_METHOD.MOMO) ? handleSwitchToMomo : undefined}
          blacklistError={blacklistError}
          userEmail={session?.user?.email}
          userId={session?.user?.id}
        />
      )}

      {/* Plan + provider confirmation immediately before checkout */}
      {showConfirmModal && selectedPlan && pendingPaymentSelection && (
        <ConfirmBeforePaymentModal
          customerName={resolvedIdentity.name || session?.user?.name || ""}
          planId={selectedPlan.id}
          planName={selectedPlan.name}
          duration={safeDuration}
          price={selectedPlan.prices?.[safeDuration]?.final || 0}
          gateway={pendingPaymentSelection.gateway}
          interviewHoursPerMonth={selectedPlan.interviewHoursPerMonth}
          emailAutoApplyCountryCount={EMAIL_AUTO_APPLY_COUNTRY_COUNT}
          onConfirm={handleConfirmAndProceed}
          onClose={() => {
            setShowConfirmModal(false);
            setPendingPaymentSelection(null);
            setShowPaymentModal(true);
          }}
        />
      )}

      {/* Payment Method Selection Modal */}
      {showPaymentModal && selectedPlan && (
        <PaymentMethodModal
          planId={selectedPlan.id}
          planName={selectedPlan.name}
          duration={safeDuration}
          onClose={() => setShowPaymentModal(false)}
          onContinue={handlePaymentMethodChosen}
        />
      )}

      {/* Flutterwave Loading Overlay */}
      {showFlutterwaveLoading && (
        <FlutterwaveLoadingOverlay
          error={flutterwaveError}
          errorCategory={flutterwaveErrorCategory}
          onRetry={flutterwaveError ? handleFlutterwaveRetry : undefined}
          onSwitchToStripe={flutterwaveError ? handleSwitchToStripe : undefined}
          onClose={flutterwaveError ? handleFlutterwaveRetry : undefined}
        />
      )}

      {/* Complete Profile Prompt Modal */}
      {profileError && (
        <CompleteProfilePrompt
          missingFields={profileError.missingFields}
          message={profileError.message}
          onClose={() => setProfileError(null)}
          onSwitchToStripe={() => {
            setProfileError(null);
            handleSwitchToStripe();
          }}
        />
      )}

      {/* Nigeria Phone + Country Prompt Modal for OPay / Bank Transfer */}
      {showNigeriaPhonePrompt && (
        <NigeriaPhonePrompt
          detectedPhone={normalizePhoneForValidation(resolvedIdentity.phone, "NG") || "missing"}
          authPhone={normalizePhoneForValidation(resolvedIdentity.authPhone, "NG") || ""}
          profilePhone={normalizePhoneForValidation(resolvedIdentity.profilePhone, "NG") || ""}
          profileCountry={resolvedIdentity.profileCountry || ""}
          selectedPaymentCountry={selectedCountry?.name || selectedCountry?.code || "Nigeria"}
          onClose={() => setShowNigeriaPhonePrompt(false)}
          onSwitchToStripe={() => {
            setShowNigeriaPhonePrompt(false);
            handleSwitchToStripe();
          }}
          onSwitchToBankTransfer={() => {
            setShowNigeriaPhonePrompt(false);
            // Bank transfer data should already be available from previous flow
            // Just close the prompt to return to bank transfer view
          }}
        />
      )}

      {/* Ghana Phone Prompt Modal for MoMo */}
      {showGhanaPhonePrompt && (
        <GhanaPhonePrompt
          detectedPhone={normalizePhoneForValidation(resolvedIdentity.phone) || "missing"}
          authPhone={normalizePhoneForValidation(resolvedIdentity.authPhone) || ""}
          profilePhone={normalizePhoneForValidation(resolvedIdentity.profilePhone) || ""}
          profileCountry={resolvedIdentity.profileCountry || ""}
          selectedPaymentCountry={selectedCountry?.name || selectedCountry?.code || "Ghana"}
          onClose={() => setShowGhanaPhonePrompt(false)}
          onSwitchToStripe={() => {
            setShowGhanaPhonePrompt(false);
            handleSwitchToStripe();
          }}
          onSwitchToBankTransfer={async () => {
            setShowGhanaPhonePrompt(false);

            if (!selectedPlan) return;

            setLoading(selectedPlan.id);
            setShowSuccess(false);
            setBankTransferData(null);
            setFlutterwaveError(null);
            setFlutterwaveErrorCategory(null);
            setProfileError(null);
            setBlacklistError(null);
            setShowFlutterwaveLoading(true);

            try {
              const response = await fetch("/api/flutterwave/create-bank-transfer", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  uid: session?.user?.id,
                  plan: selectedPlan.id,
                  interval: safeDuration,
                  interviewHoursPerMonth: selectedPlan?.interviewHoursPerMonth,
                }),
              });

              let data = null;
              try {
                data = await response.json();
              } catch (parseError) {
                console.error("[PricingTable] Failed to parse Flutterwave response:", parseError);
                setBlacklistError({
                  message: t.alert_error,
                  supportEmail: SUPPORT_EMAIL,
                });
                setBankTransferData({
                  isBlacklisted: true,
                  bankName: "",
                  accountNumber: "",
                  amount: 0,
                  currency: "GHS",
                  tx_ref: "",
                });
                setShowFlutterwaveLoading(false);
                return;
              }

              if (!response.ok) {
                console.error(
                  "[PricingTable] Flutterwave bank transfer error:",
                  data.error || data.message
                );

                if (data.errorCode === "CUSTOMER_PROFILE_INCOMPLETE") {
                  setProfileError({
                    errorCode: data.errorCode,
                    missingFields: data.missingFields || [],
                    message:
                      data.message ||
                      tp.complete_profile_desc,
                  });
                  setShowFlutterwaveLoading(false);
                  return;
                }

                if (data.errorCode === "EMAIL_BLACKLISTED") {
                  setBlacklistError({
                    message: data.message || ERROR_MESSAGES.EMAIL_BLACKLISTED,
                    supportEmail: data.supportEmail || SUPPORT_EMAIL,
                  });
                  setBankTransferData({
                    isBlacklisted: true,
                    bankName: "",
                    accountNumber: "",
                    amount: 0,
                    currency: "GHS",
                    tx_ref: "",
                  });
                  setShowFlutterwaveLoading(false);
                  return;
                }

                if (data.errorCode === "FLW_ERROR") {
                  setBlacklistError({
                    message:
                      data.message ||
                      tp.couldnt_process,
                    supportEmail: SUPPORT_EMAIL,
                  });
                  setBankTransferData({
                    isBlacklisted: true,
                    bankName: "",
                    accountNumber: "",
                    amount: 0,
                    currency: "GHS",
                    tx_ref: "",
                  });
                  setShowFlutterwaveLoading(false);
                  return;
                }

                if (data.errorCode === "FLW_BANK_TRANSFER_NOT_ENABLED" || data.errorCode === "FLW_GH_BANK_TRANSFER_NOT_ENABLED") {
                  setBlacklistError({
                    message: data.message || tp.flw_bank_unavailable,
                    supportEmail: SUPPORT_EMAIL,
                  });
                  setBankTransferData({
                    isBlacklisted: true,
                    bankName: "",
                    accountNumber: "",
                    amount: 0,
                    currency: "GHS",
                    tx_ref: "",
                  });
                  setShowFlutterwaveLoading(false);
                  return;
                }

                setClassifiedFlutterwaveError(
                  data.message || data.error || t.alert_error,
                  data.errorCategory || null
                );
                setShowFlutterwaveLoading(false);
                return;
              }

              setShowFlutterwaveLoading(false);
              if (data.accountNumber && data.bankName) {
                setBankTransferData(data);
              }
            } catch (error) {
              console.error("[PricingTable] Error:", error);
              setClassifiedFlutterwaveError("An error occurred. Please try again.", "gateway_error");
              setShowFlutterwaveLoading(true);
            } finally {
              setLoading(null);
            }
          }}
        />
      )}

      {/* Kenya Phone Prompt Modal for M-Pesa */}
      {showKenyaPhonePrompt && (
        <KenyaPhonePrompt
          detectedPhone={normalizePhoneForValidation(resolvedIdentity.phone, "KE") || "missing"}
          authPhone={normalizePhoneForValidation(resolvedIdentity.authPhone, "KE") || ""}
          profilePhone={normalizePhoneForValidation(resolvedIdentity.profilePhone, "KE") || ""}
          profileCountry={resolvedIdentity.profileCountry || ""}
          selectedPaymentCountry={selectedCountry?.name || selectedCountry?.code || "Kenya"}
          onClose={() => setShowKenyaPhonePrompt(false)}
          onSwitchToStripe={() => {
            setShowKenyaPhonePrompt(false);
            handleSwitchToStripe();
          }}
        />
      )}

      {/* Rwanda Phone Prompt Modal for MoMo */}
      {showRwandaPhonePrompt && (
        <RwandaPhonePrompt
          detectedPhone={normalizePhoneForValidation(resolvedIdentity.phone, "RW") || "missing"}
          authPhone={normalizePhoneForValidation(resolvedIdentity.authPhone, "RW") || ""}
          profilePhone={normalizePhoneForValidation(resolvedIdentity.profilePhone, "RW") || ""}
          profileCountry={resolvedIdentity.profileCountry || ""}
          selectedPaymentCountry={selectedCountry?.name || selectedCountry?.code || "Rwanda"}
          onClose={() => setShowRwandaPhonePrompt(false)}
          onSwitchToStripe={() => {
            setShowRwandaPhonePrompt(false);
            handleSwitchToStripe();
          }}
        />
      )}

      {/* Tanzania Phone Prompt Modal for MoMo */}
      {showTanzaniaPhonePrompt && (
        <TanzaniaPhonePrompt
          detectedPhone={normalizePhoneForValidation(resolvedIdentity.phone, "TZ") || "missing"}
          authPhone={normalizePhoneForValidation(resolvedIdentity.authPhone, "TZ") || ""}
          profilePhone={normalizePhoneForValidation(resolvedIdentity.profilePhone, "TZ") || ""}
          profileCountry={resolvedIdentity.profileCountry || ""}
          selectedPaymentCountry={selectedCountry?.name || selectedCountry?.code || "Tanzania"}
          onClose={() => setShowTanzaniaPhonePrompt(false)}
          onSwitchToStripe={() => {
            setShowTanzaniaPhonePrompt(false);
            handleSwitchToStripe();
          }}
        />
      )}

      {/* Uganda Phone Prompt Modal for MoMo */}
      {showUgandaPhonePrompt && (
        <UgandaPhonePrompt
          detectedPhone={normalizePhoneForValidation(resolvedIdentity.phone, "UG") || "missing"}
          authPhone={normalizePhoneForValidation(resolvedIdentity.authPhone, "UG") || ""}
          profilePhone={normalizePhoneForValidation(resolvedIdentity.profilePhone, "UG") || ""}
          profileCountry={resolvedIdentity.profileCountry || ""}
          selectedPaymentCountry={selectedCountry?.name || selectedCountry?.code || "Uganda"}
          onClose={() => setShowUgandaPhonePrompt(false)}
          onSwitchToStripe={() => {
            setShowUgandaPhonePrompt(false);
            handleSwitchToStripe();
          }}
        />
      )}

      {/* Zambia Phone Prompt Modal for MoMo */}
      {showZambiaPhonePrompt && (
        <ZambiaPhonePrompt
          detectedPhone={normalizePhoneForValidation(resolvedIdentity.phone, "ZM") || "missing"}
          authPhone={normalizePhoneForValidation(resolvedIdentity.authPhone, "ZM") || ""}
          profilePhone={normalizePhoneForValidation(resolvedIdentity.profilePhone, "ZM") || ""}
          profileCountry={resolvedIdentity.profileCountry || ""}
          selectedPaymentCountry={selectedCountry?.name || selectedCountry?.code || "Zambia"}
          onClose={() => setShowZambiaPhonePrompt(false)}
          onSwitchToStripe={() => {
            setShowZambiaPhonePrompt(false);
            handleSwitchToStripe();
          }}
        />
      )}

      {/* South Africa Phone Prompt Modal for MoMo */}
      {showSouthAfricaPhonePrompt && (
        <SouthAfricaPhonePrompt
          detectedPhone={normalizePhoneForValidation(resolvedIdentity.phone, "ZA") || "missing"}
          authPhone={normalizePhoneForValidation(resolvedIdentity.authPhone, "ZA") || ""}
          profilePhone={normalizePhoneForValidation(resolvedIdentity.profilePhone, "ZA") || ""}
          profileCountry={resolvedIdentity.profileCountry || ""}
          selectedPaymentCountry={selectedCountry?.name || selectedCountry?.code || "South Africa"}
          onClose={() => setShowSouthAfricaPhonePrompt(false)}
          onSwitchToStripe={() => {
            setShowSouthAfricaPhonePrompt(false);
            handleSwitchToStripe();
          }}
        />
      )}

      {/* Egypt Phone Prompt Modal for MoMo */}
      {showEgyptPhonePrompt && (
        <EgyptPhonePrompt
          detectedPhone={normalizePhoneForValidation(resolvedIdentity.phone, "EG") || "missing"}
          authPhone={normalizePhoneForValidation(resolvedIdentity.authPhone, "EG") || ""}
          profilePhone={normalizePhoneForValidation(resolvedIdentity.profilePhone, "EG") || ""}
          profileCountry={resolvedIdentity.profileCountry || ""}
          selectedPaymentCountry={selectedCountry?.name || selectedCountry?.code || "Egypt"}
          onClose={() => setShowEgyptPhonePrompt(false)}
          onSwitchToStripe={() => {
            setShowEgyptPhonePrompt(false);
            handleSwitchToStripe();
          }}
        />
      )}

      {/* Francophone Phone Prompt Modal for XOF/XAF MoMo */}
      {showFrancophonePhonePrompt && francophonePhoneSubCountry && (
        <FrancophonePhonePrompt
          detectedPhone={normalizePhoneForValidation(resolvedIdentity.phone, francophonePhoneSubCountry) || "missing"}
          authPhone={normalizePhoneForValidation(resolvedIdentity.authPhone, francophonePhoneSubCountry) || ""}
          profilePhone={normalizePhoneForValidation(resolvedIdentity.profilePhone, francophonePhoneSubCountry) || ""}
          profileCountry={resolvedIdentity.profileCountry || ""}
          selectedPaymentCountry={COUNTRY_CODE_TO_NAME[francophonePhoneSubCountry] || francophonePhoneSubCountry}
          subCountryCode={francophonePhoneSubCountry}
          onClose={() => {
            setShowFrancophonePhonePrompt(false);
            setFrancophonePhoneSubCountry(null);
          }}
          onSwitchToStripe={() => {
            setShowFrancophonePhonePrompt(false);
            setFrancophonePhoneSubCountry(null);
            handleSwitchToStripe();
          }}
        />
      )}

      {/* Sub-Country Selection Modal for XOF/XAF */}
      {subCountrySelectionData && !subCountrySelectionData.selectedSubCountry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <button
              onClick={() => {
                setSubCountrySelectionData(null);
                setShowFlutterwaveLoading(false);
                setLoading(null);
              }}
              className="absolute right-4 top-4 rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            >
              <X size={20} />
            </button>
            
            <h2 className="mb-2 text-xl font-bold text-slate-900">
              Select Your Country
            </h2>
            <p className="mb-4 text-sm text-slate-600">
              {subCountrySelectionData.regionName}: choose your country to see available mobile money providers
            </p>
            
            <div className="space-y-2">
              {subCountrySelectionData.subCountries.map((sc) => (
                <button
                  key={sc.code}
                  onClick={() => {
                    console.log(`[PricingTable] Sub-country selected: ${sc.code} (${sc.name})`);

                    // Validate phone against the selected sub-country prefix
                    const scCode = sc.code.toUpperCase();
                    const normalizedPhone = normalizePhoneForValidation(resolvedIdentity.phone, scCode);
                    const phoneOk = phoneMatchesMomoCountry(normalizedPhone, scCode);
                    if (!normalizedPhone || !phoneOk) {
                      console.log(`[PricingTable] Phone mismatch for sub-country ${scCode}:`, normalizedPhone || "missing");
                      setSubCountrySelectionData((prev) => ({
                        ...prev,
                        selectedSubCountry: scCode,
                      }));
                      setFrancophonePhoneSubCountry(scCode);
                      setShowFrancophonePhonePrompt(true);
                      setShowFlutterwaveLoading(false);
                      setLoading(null);
                      return;
                    }

                    // Set the selected sub-country
                    setSubCountrySelectionData((prev) => ({
                      ...prev,
                      selectedSubCountry: sc.code,
                    }));

                    setNetworkSelectionData({
                      supportedNetworks: sc.operators, // Already { key, label } objects
                      countryCode: subCountrySelectionData.regionCode,
                      subCountryCode: sc.code,
                      message: tp.select_mobile_provider,
                      retryPayload: {
                        uid: session?.user?.id,
                        plan: selectedPlan?.id,
                        interval: safeDuration,
                        interviewHoursPerMonth: selectedPlan?.interviewHoursPerMonth,
                        phoneNumber: resolvedIdentity?.phone || "",
                        countryCode: subCountrySelectionData.regionCode,
                      },
                    });
                    setShowFlutterwaveLoading(false);
                    setLoading(null);
                  }}
                  className="w-full rounded-lg border-2 border-slate-200 bg-white px-4 py-3 text-left font-semibold text-slate-900 transition hover:border-orange-500 hover:bg-orange-50"
                >
                  <span className="block">{localizedCountryName(sc.name, locale)}</span>
                  <span className="block text-xs font-normal text-slate-500">
                    {sc.operators.map(op => op.label).join(", ")}
                  </span>
                </button>
              ))}
            </div>
            
            <div className="mt-4 rounded-lg border-2 border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-amber-900 mb-2 text-center">
                {tp.country_not_listed}
              </p>
              <button
                onClick={() => {
                  setSubCountrySelectionData(null);
                  setShowFlutterwaveLoading(false);
                  setLoading(null);
                  handleSwitchToStripe();
                }}
                className="w-full rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition"
              >
                {tp.pay_with_card}
              </button>
              <p className="text-xs text-amber-700 mt-1.5 text-center">
                {tp.card_any_country}
              </p>
            </div>
            
            <button
              onClick={() => {
                setSubCountrySelectionData(null);
                setShowFlutterwaveLoading(false);
                setLoading(null);
              }}
              className="mt-4 w-full rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              {tp.cancel}
            </button>
          </div>
        </div>
      )}

      {/* Network Selection Modal for MoMo */}
      {networkSelectionData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <button
              type="button"
              aria-label={tp.close}
              onClick={() => {
                setNetworkSelectionData(null);
                setShowFlutterwaveLoading(false);
                setLoading(null);
              }}
              className="absolute right-4 top-4 rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            >
              <X size={20} />
            </button>
            
            <h2 className="mb-2 text-xl font-bold text-slate-900">
              {tp.select_provider_title}
            </h2>
            <p className="mb-4 text-sm text-slate-600">
              {networkSelectionData.message}
            </p>
            
            <div className="space-y-2">
              {networkSelectionData.supportedNetworks.map((networkInfo) => {
                // networkInfo is now an object with { key, label }
                const networkKey = typeof networkInfo === 'string' ? networkInfo : networkInfo.key;
                const displayName = typeof networkInfo === 'string' 
                  ? (MOMO_NETWORK_DISPLAY_NAMES[networkInfo.toLowerCase()] || networkInfo)
                  : networkInfo.label;
                
                return (
                  <button
                    key={networkKey}
                    onClick={async () => {
                      console.log(`[PricingTable] Network selected: ${networkKey} (${displayName})`);
                      
                      // Close network selection modal and show loading overlay immediately
                      setNetworkSelectionData(null);
                      setShowFlutterwaveLoading(true);
                      
                      // Retry the payment with the selected network
                      try {
                        const response = await fetch("/api/flutterwave/create-momo-charge", {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                          },
                          body: JSON.stringify({
                            ...networkSelectionData.retryPayload,
                            network: networkKey, // Add the selected network key (API key)
                            ...(networkSelectionData.subCountryCode
                              ? { subCountryCode: networkSelectionData.subCountryCode }
                              : {}),
                          }),
                        });

                        const data = await response.json();

                        if (!response.ok) {
                          console.error("[PricingTable] MoMo charge error with network:", data.error || data.message);
                          setClassifiedFlutterwaveError(
                            data.message || data.error || t.alert_error,
                            data.errorCategory || null
                          );
                          setShowFlutterwaveLoading(true);
                          setLoading(null);
                          return;
                        }

                        // If successful, hide loading and show mobile wallet details modal
                        setShowFlutterwaveLoading(false);
                        setLoading(null);
                        // Show modal for:
                        // 1. OPay redirect (has redirect_url/payment_url)
                        // 2. MoMo pending (push/prompt flows often return pending without a URL/instructions)
                        const hasRedirectUrl = Boolean(data.redirect_url || data.payment_url);
                        const isPending = data.status === "pending";
                        const retryCountryCode = (networkSelectionData.retryPayload?.countryCode || networkSelectionData.countryCode || "GH").toUpperCase();

                        if (isPending && !hasRedirectUrl && !data.instructions) {
                          data.instructions = `${tp.approve_momo_prompt}. ${tp.keep_open_60s}`;
                        }

                        if (hasRedirectUrl || isPending) {
                          const retryWalletData = { ...data };
                          const retryWalletTypeMap = {
                            GH: WALLET_TYPE_GHANA_MOMO,
                            KE: WALLET_TYPE_KENYA_MOMO,
                            RW: WALLET_TYPE_RWANDA_MOMO,
                            TZ: WALLET_TYPE_TANZANIA_MOMO,
                            UG: WALLET_TYPE_UGANDA_MOMO,
                            ZM: WALLET_TYPE_ZAMBIA_MOMO,
                            XOF: WALLET_TYPE_XOF_MOMO,
                            XAF: WALLET_TYPE_XAF_MOMO,
                          };
                          const resolvedRetryWalletType = retryWalletTypeMap[retryCountryCode] || WALLET_TYPE_GHANA_MOMO;
                          setMobileWalletData(retryWalletData);
                          setWalletType(resolvedRetryWalletType);
                          // Persist to localStorage so polling resumes after refresh
                          savePendingMomoPayment(retryWalletData, resolvedRetryWalletType, retryCountryCode);
                        }
                      } catch (error) {
                        console.error("[PricingTable] Network retry error:", error);
                        setClassifiedFlutterwaveError(t.alert_error, "gateway_error");
                        setShowFlutterwaveLoading(true);
                        setLoading(null);
                      }
                    }}
                    className="w-full rounded-lg border-2 border-slate-200 bg-white px-4 py-3 text-left font-semibold text-slate-900 transition hover:border-orange-500 hover:bg-orange-50"
                  >
                    {displayName}
                  </button>
                );
              })}
            </div>
            
            {networkSelectionData.countryCode !== "KE" && (
              <div className="mt-4 rounded-lg border-2 border-amber-200 bg-amber-50 p-4">
                <p className="text-sm font-semibold text-amber-900 mb-2 text-center">
                  {tp.provider_not_found}
                </p>
                <button
                  onClick={() => {
                    setNetworkSelectionData(null);
                    setShowFlutterwaveLoading(false);
                    setLoading(null);
                    handleSwitchToStripe();
                  }}
                  className="w-full rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition"
                >
                  {tp.pay_with_card}
                </button>
                <p className="text-xs text-amber-700 mt-1.5 text-center">
                  {tp.card_any_country}
                </p>
              </div>
            )}
            
            <button
              onClick={() => {
                setNetworkSelectionData(null);
                setShowFlutterwaveLoading(false);
                setLoading(null);
              }}
              className="mt-4 w-full rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              {tp.cancel}
            </button>
          </div>
        </div>
      )}

      {/* Mobile Wallet Details Modal */}
      {mobileWalletData && walletType && (
        <MobileWalletDetails
          paymentUrl={mobileWalletData.payment_url}
          walletType={walletType}
          amountNgn={mobileWalletData.amount}
          txRef={mobileWalletData.tx_ref}
          countryCode={selectedCountry?.code || restoredCountryCode}
          currentMethod={walletType === "opay" ? PAYMENT_METHOD.OPAY : PAYMENT_METHOD.MOMO}
          onComplete={() => {
            clearPendingMomoPayment();
            setShowSuccess(true);
          }}
          onCancel={() => {
            clearPendingMomoPayment();
            setMobileWalletData(null);
            setWalletType(null);
            setRestoredCountryCode(null);
            setNetworkSelectionData(null); // Clear network selection to allow fresh retry
          }}
          onSwitchToStripe={() => {
            clearPendingMomoPayment();
            setMobileWalletData(null);
            setWalletType(null);
            setRestoredCountryCode(null);
            setNetworkSelectionData(null);
            setShowFlutterwaveLoading(false);
            setLoading(null);
            handleSwitchToStripe();
          }}
          onSwitchToBankTransfer={isMethodEnabled(selectedCountry?.code, PAYMENT_METHOD.BANK_TRANSFER) ? handleSwitchToBankTransfer : undefined}
          onSwitchToOpay={selectedCountry?.code === "NG" && walletType !== "opay" ? handleSwitchToOpay : undefined}
          onSwitchToMomo={isMethodEnabled(selectedCountry?.code, PAYMENT_METHOD.MOMO) && walletType === "opay" ? handleSwitchToMomo : undefined}
          currency={mobileWalletData.currency}
          status={mobileWalletData.status}
          instructions={mobileWalletData.instructions}
          authorizationRedirect={mobileWalletData.authorizationRedirect || null}
          qrImage={mobileWalletData.qrImage || null}
        />
      )}

      {/* Success Modal */}
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-center">
              <div className="rounded-full bg-emerald-100 p-4">
                <Check className="h-12 w-12 text-emerald-600" />
              </div>
            </div>
            <h2 className="mb-2 text-center text-2xl font-bold text-slate-900">
              Payment Confirmed!
            </h2>
            <p className="mb-6 text-center text-sm text-slate-600">
              Your subscription is now active. Thank you for choosing Jobolar!
            </p>
            <button
              onClick={() => {
                setShowSuccess(false);
                router.push("/profile?tab=automation");
              }}
              className="w-full rounded-md bg-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 transition"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
